import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useInfoNutricional, type InfoNutricionalRequest, INFO_NUTRICIONAL_DEFAULT } from "@/features/produtos/hooks/useInfoNutricional";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Save, Search, Plus, Pencil, Trash2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { produtoSchema, mapToApiRequest, type ProdutoFormData } from "@/features/produtos/schemas/produtoSchema";
import { useProduto, useCreateProduto, useUpdateProduto } from "@/features/produtos/hooks/useProdutos";
import { useProductGroups } from "@/features/produtos/hooks/useProductGroups";
import { useProductSubgroups } from "@/features/produtos/hooks/useProductSubgroups";
import { useProductSections } from "@/features/produtos/hooks/useProductSections";
import { useProductSubsections } from "@/features/produtos/hooks/useProductSubsections";
import { useProductBrands } from "@/features/produtos/hooks/useProductBrands";
import { useProductModels } from "@/features/produtos/hooks/useProductModels";
import { useTiposMaterial } from "@/features/produtos/hooks/useTiposMaterial";
import { useProdutoContabil } from "@/features/produtos/hooks/useProdutoContabil";
import { ProdutoContabilTable } from "@/features/produtos/components/ProdutoContabilTable";
import { useProdutoLocaisEstoque, type ProdutoLocalEstoqueRow, type SalvarLocalEstoqueRequest } from "@/features/produtos/hooks/useProdutoLocaisEstoque";
import { LocalEstoqueDialog } from "@/features/produtos/components/LocalEstoqueDialog";
import { useProdutoImagens } from "@/features/produtos/hooks/useProdutoImagens";
import { ProdutoImageSlot } from "@/features/produtos/components/ProdutoImageSlot";
import { useProdutoFornecedores, type ProdutoFornecedorRow, type SalvarFornecedorRequest } from "@/features/produtos/hooks/useProdutoFornecedores";
import { FornecedorDialog } from "@/features/produtos/components/FornecedorDialog";
import { useProdutoComposicao, useProdutoAdicionais, useProdutoOpcionais, useOpcionaisCombo } from "@/features/produtos/hooks/useProdutoItens";
import { useProdutoIcms, type ProdutoIcmsRow, type SalvarProdutoIcmsRequest } from "@/features/produtos/hooks/useProdutoIcms";
import { ProdutoIcmsDialog } from "@/features/produtos/components/ProdutoIcmsDialog";
import { useProdutoGrades } from "@/features/produtos/hooks/useProdutoGrades";
import { useGradesX } from "@/features/produtos/hooks/useGradesX";
import { useGradesY } from "@/features/produtos/hooks/useGradesY";
import { useProdutoComissoes, usePerfisCombo } from "@/features/produtos/hooks/useProdutoComissoes";
import { useProdutoSaldo } from "@/features/produtos/hooks/useProdutoSaldo";
import { useGarantias } from "@/features/produtos/hooks/useGarantias";
import MedicamentoSngpc from "./MedicamentoSngpc";
import { useAuth } from "@/hooks/useAuth";
import { useTipoEmpresaAtual } from "@/features/empresa/hooks/useEmpresa";
import { ehFarmacia } from "@/lib/tipoEmpresa";
import { ProdutoSearchCombobox } from "@/components/ProdutoSearchCombobox";
import { useProductUnits } from "@/features/produtos/hooks/useProductUnits";
import { CfopSearchCombobox } from "@/components/CfopSearchCombobox";
import {
  useCstIcmsCombo, useCstPisCofinsCombo, useCstsIpiCombo,
  useCsosnCombo, useOrigensCombo, useCClassTribCombo, useNaturezasReceitaCombo,
} from "@/features/fiscal/hooks/useFiscal";
import { AnpSearchCombobox } from "@/components/AnpSearchCombobox";
import { useDebounce } from "@/hooks/useDebounce";
import { apiClient } from "@/lib/apiClient";
import type { ProdutoDetalheApi } from "@/features/produtos/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage,
} from "@/components/ui/form";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";

// ── Tipos fiscais ─────────────────────────────────────────────────────────────

interface ClassificacaoFiscalItem {
  id: number;
  ncmId: number;
  codigo: string;
  descricao: string;
  codigoNcmComplemento?: string;
  cClassTribId?: number;
  incidePisCofins: boolean;
  incideIpi: boolean;
  isMonofasico: boolean;
}

interface NcmItem {
  codigoInterno: number;
  codigo: string;
  descricao: string;
  isAtivo: boolean;
  usaCest: boolean;
}

interface CestItem {
  id: number;
  codigo: string;
  descricao: string;
}

interface LocalEstoqueItem {
  id: number;
  localEstoqueId: number;
  localEstoqueDescricao: string;
  unidadeEntradaId: number;
  unidadeSaidaId: number;
  unidadeEntradaTributavelId: number;
  unidadeSaidaTributavelId: number;
  fatorConversaoEntrada: number;
  fatorConversaoSaida: number;
  fatorConversaoEntradaTributavel: number;
  fatorConversaoSaidaTributavel: number;
  vendaFracionada: boolean;
  padraoEntrada: boolean;
  padraoSaida: boolean;
  estoqueMinimo: number;
  estoqueMaximo: number;
  custoMedio: number;
  ultimaCompra: number;
  dtUltimaCompra: string;
  precoReposicao: number;
  precoGerencial: number;
  volume: number;
  unidadeVolume?: string;
  peso: number;
  unidadePeso?: string;
}

// ── Constantes ────────────────────────────────────────────────────────────────

const TIPO_PRODUTO_OPTIONS = [
  { value: 1, label: "Mercadoria" },
  { value: 2, label: "Matéria Prima" },
  { value: 3, label: "Semi-Acabado" },
  { value: 4, label: "Produto Acabado" },
  { value: 5, label: "Embalagem" },
  { value: 6, label: "Uso/Consumo" },
  { value: 7, label: "Ativo Imobilizado" },
  { value: 8, label: "Serviço" },
  { value: 9, label: "Combustível" },
];

const TIPO_MARGEM_OPTIONS = [
  { value: 1, label: "Sobre Custo" },
  { value: 2, label: "Sobre Venda" },
];

// ── Mappers ───────────────────────────────────────────────────────────────────

function mapToFormData(p: ProdutoDetalheApi): ProdutoFormData {
  return {
    descricao_completa:   p.descricao,
    descricao_resumida:   p.descricaoReduzida   ?? null,
    codigo_barras:        p.codigoBarras         ?? null,
    gtin:                 p.gtin                ?? null,
    tipo_produto:         p.tipoProduto,
    tipo_material_id:     p.tipoMaterialId,
    classificacao:        p.classificacao ?? null,
    inativo:              p.inativo,
    grupo_id:             p.grupoId,
    sub_grupo_id:         p.subgrupoId,
    secao_id:             p.secaoId,
    sub_secao_id:         p.subsecaoId,
    marca_id:             p.marcaId,
    modelo_id:            p.modeloId,
    cor:                  p.cor                 ?? null,
    tamanho:              p.tamanho             ?? null,
    sabor:                p.sabor               ?? null,
    ncm_id:                     p.ncmId,
    cest:                       p.cest                  ?? null,
    aliquota_icms:              p.aliquotaIcms,
    aliquota_icms_st:           p.aliquotaIcmsSt,
    aliquota_ipi:               p.aliquotaIpi,
    percentual_tributos:        p.percentualTributosInformado,
    csosn_id:                   p.csosnId,
    origem_id:                  p.origemId,
    cst_id:                     p.cstId,
    cclass_trib:                p.cclassTrib ?? null,
    id_pos:                     p.idPos ?? null,
    anp_id:                     p.anpId,
    natureza_receita_id:        p.naturezaReceitaId,
    cfop_saida_exterior:        p.cfopSaidaExterior,
    cfop_entrada_exterior:      p.cfopEntradaExterior,
    perc_pis_dif:               p.percentualPisDif,
    perc_cofins_dif:            p.percentualCofinsDif,
    exportavel:                 p.exportavel,
    veiculo_usado:              p.veiculoUsado,
    aliq_cbs:                   p.aliqCbs,
    aliq_ibs_uf:                p.aliqIbsUf,
    aliq_ibs_municipio:         p.aliqIbsMunicipio,
    valor_lucro_zero:           p.valorLucroZero,
    codigo_ml:                  p.codigoMl ?? null,
    garantia:                   p.garantia ?? null,
    garantia_id:                p.garantiaId ?? 0,
    tipo_especifico:            p.tipoEspecifico ?? 0,
    tipo_arma:                  p.tipoArma ?? null,
    serie_arma:                 p.serieArma ?? null,
    serie_cano:                 p.serieCano ?? null,
    descricao_anp:              p.descricaoAnp ?? null,
    perc_gas_natural:           p.percGasNatural ?? 0,
    perc_glp:                   p.percGlp ?? 0,
    perc_glgn_nacional:         p.percGlgnNacional ?? 0,
    perc_glgn_importado:        p.percGlgnImportado ?? 0,
    codif:                      p.codif ?? null,
    valor_partida:              p.valorPartida ?? 0,
    numero_lote:                p.numeroLote ?? null,
    quantidade_lote:            p.quantidadeLote ?? 0,
    data_fabricacao:            p.dataFabricacao ? p.dataFabricacao.substring(0, 10) : null,
    data_validade:              p.dataValidade ? p.dataValidade.substring(0, 10) : null,
    preco_max_consumidor:       p.precoMaximoConsumidor ?? 0,
    numero_recopi:              p.numeroRecopi ?? null,
    mve:                        p.mve ?? 0,
    custo_medio:                p.custoMedio ?? 0,
    custo_gerencial:            p.custoGerencial ?? 0,
    custo_reposicao:            p.custoReposicao ?? 0,
    custos_extras:              p.custosExtras ?? 0,
    custo_frete:                p.custoFrete ?? 0,
    custo_seguro:               p.custoSeguro ?? 0,
    custo_outras_despesas:      p.custoOutrasDespesas ?? 0,
    custo_embalagem:            p.custoEmbalagem ?? 0,
    perc_custo_operacional:     p.percCustoOperacional ?? 0,
    lucro_desejado:             p.lucroDesejado ?? 0,
    perc_lucro_cadastrado:      p.percLucroCadastrado ?? 0,
    markup:                     p.markup ?? 0,
    preco_sugestao:             p.precoSugestao ?? 0,
    preco_venda1:               p.precoVenda1 ?? 0,
    preco_venda2:               p.precoVenda2 ?? 0,
    preco_atacado:              p.precoAtacado ?? 0,
    preco_promocao:             p.precoPromocao ?? 0,
    desconto_maximo:            p.descontoMaximo ?? 0,
    perc_comissao:              p.percComissao ?? 0,
    comissao_por:               p.comissaoPor ?? 0,
    movimenta_comissao_diferenciada: p.movimentaComissaoDiferenciada ?? false,
    cst_pis_cofins_entrada_id:  p.cstPisCofinsEntradaId,
    cst_pis_cofins_saida_id:    p.cstPisCofinsSaidaId,
    cst_ipi_entrada_id:         p.cstIpiEntradaId,
    cst_ipi_saida_id:           p.cstIpiSaidaId,
    incide_ipi:                 p.incideIpi,
    incide_pis_cofins:          p.incidePisCofins,
    monofasico:                 p.monofasico,
    cfop_saida:                 p.cfopSaida,
    cfop_saida_nao_contribuinte: p.cfopSaidaNaoContribuinte,
    cfop_saida_uf:              p.cfopSaidaUf,
    cfop_saida_uf_nao_contribuinte: p.cfopSaidaUfNaoContribuinte,
    cfop_entrada:               p.cfopEntrada,
    cfop_entrada_uf:            p.cfopEntradaUf,
    preco_padrao:         p.precoPadrao,
    tipo_margem_lucro:    p.tipoMargemLucro,
    percentual_margem:    p.percentualMargem,
    percentual_desconto:  p.percentualDesconto,
    valor_margem:         p.valorMargem,
    controla_estoque:        p.controlaEstoque,
    controla_lote:           p.controlaLote,
    perecivel:               p.perecivel,
    dias_validade:           p.diasValidade,
    conservacao:             p.conservacao            ?? null,
    rua:                     p.rua                    ?? null,
    coluna:                  p.coluna                 ?? null,
    nivel:                   p.nivel                  ?? null,
    prateleira:              p.prateleira             ?? null,
    consulta_peso:           p.consultaPeso,
    gera_carga_balanca:      p.geraCargaBalanca,
    mercadoria_producao:     p.mercadoriaProducao,
    imprimir_data_embalagem: p.imprimeDataEmbalagem,
    imprimir_observacao:     p.imprimeObservacao,
    monta_obs_ficha_tecnica: p.montaObsFichaTecnica,
    mostrar_celular:         p.mostrarCelular,
    mostrar_catalogo:        p.mostrarCatalogo,
    mostrar_ecommerce:       p.mostrarEcommerce,
    gera_comissao:           p.geraComissao,
    pontua_fidelidade:       p.pontuaFidelidade,
    possui_composicao:       p.possuiComposicao,
    possui_adicionais:       p.possuiAdicionais,
    usa_grade:               p.usaGrade ?? false,
    observacao:           p.observacao          ?? null,
    path_imagem:          p.pathImagem          ?? null,
  };
}

// ── Helper: linha de switch ───────────────────────────────────────────────────

const SwitchRow = ({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) => (
  <div className="flex items-center justify-between rounded-lg border p-3 gap-4">
    <div>
      <p className="text-sm font-medium leading-none">{label}</p>
      {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
    </div>
    <Switch checked={checked} onCheckedChange={onChange} />
  </div>
);

// Select fiscal reutilizável (FK por id; "__none__" = não definido → 0)
function FiscalComboField({ control, name, label, options, placeholder = "Selecione" }: {
  control: any; name: string; label: string;
  options: { id: number; label: string }[]; placeholder?: string;
}) {
  return (
    <FormField control={control} name={name as never} render={({ field }: any) => (
      <FormItem>
        <FormLabel>{label}</FormLabel>
        <Select value={field.value ? String(field.value) : "__none__"} onValueChange={(v) => field.onChange(v === "__none__" ? 0 : Number(v))}>
          <FormControl><SelectTrigger><SelectValue placeholder={placeholder} /></SelectTrigger></FormControl>
          <SelectContent>
            <SelectItem value="__none__">—</SelectItem>
            {options.map((o) => <SelectItem key={o.id} value={String(o.id)}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <FormMessage />
      </FormItem>
    )} />
  );
}

// ── Componente principal ──────────────────────────────────────────────────────

const ProdutoFormPage = () => {
  const navigate   = useNavigate();
  const { id }     = useParams();
  const isEdit     = !!id;
  const { user }   = useAuth();
  // Aba SNGPC só para Farmácia (mesma regra do Desktop); ramo desconhecido = mostra.
  const tipoEmpresa = useTipoEmpresaAtual(user?.empresa?.id);
  const mostraSngpc = tipoEmpresa === null || ehFarmacia(tipoEmpresa);

  const { data: produto, isLoading } = useProduto(id);
  const createMutation = useCreateProduto();
  const updateMutation = useUpdateProduto();

  const { groups }      = useProductGroups();
  const { subgroups }   = useProductSubgroups();
  const { sections }    = useProductSections();
  const { subsections } = useProductSubsections();
  const { brands }      = useProductBrands();
  const { models }      = useProductModels();
  const { garantias }   = useGarantias();
  const { tiposMaterial } = useTiposMaterial();
  const { registros: contabilRegistros, createReg: createContabil, deleteReg: deleteContabil, isCreating: isCreatingContabil } = useProdutoContabil(id);
  const { locais: locaisEstoque, createLocal, updateLocal, deleteLocal } = useProdutoLocaisEstoque(id);
  const { status: imagensStatus, upload: uploadImagem, remove: removeImagem } = useProdutoImagens(id);
  const { fornecedores, createForn, updateForn, deleteForn } = useProdutoFornecedores(id);
  const [fornDialogOpen, setFornDialogOpen] = useState(false);
  const [fornEditing, setFornEditing] = useState<ProdutoFornecedorRow | null>(null);
  const saveForn = (data: SalvarFornecedorRequest, rid?: number) => {
    if (rid) updateForn({ id: rid, data }); else createForn(data);
    setFornDialogOpen(false);
  };
  const [localDialogOpen, setLocalDialogOpen] = useState(false);
  const [localEditing, setLocalEditing] = useState<ProdutoLocalEstoqueRow | null>(null);
  const saveLocal = (data: SalvarLocalEstoqueRequest, rid?: number) => {
    if (rid) updateLocal({ id: rid, data }); else createLocal(data);
    setLocalDialogOpen(false);
  };

  // ── Combos fiscais (Select em vez de texto livre) ─────────────────────────
  const cstIcmsList    = (useCstIcmsCombo().data ?? []).filter(c => c.cst && c.cst.length <= 2);
  const cstPisList     = useCstPisCofinsCombo().data ?? [];
  const cstIpiList     = useCstsIpiCombo().data ?? [];
  const csosnList      = useCsosnCombo().data ?? [];
  const origensList    = useOrigensCombo().data ?? [];
  const cClassTribList = useCClassTribCombo().data ?? [];
  const natReceitaList = useNaturezasReceitaCombo().data ?? [];

  // ── Info nutricional ──────────────────────────────────────────────────────
  const produtoIdNum = id ? parseInt(id) : undefined;
  const { infoNutricional, isSaving: isSavingNutri, saveInfoNutricional } = useInfoNutricional(produtoIdNum);
  const [nutri, setNutri] = useState<InfoNutricionalRequest>(INFO_NUTRICIONAL_DEFAULT);

  useEffect(() => {
    if (infoNutricional) {
      const { id: _id, produtoId: _pid, ...rest } = infoNutricional;
      setNutri(rest);
    }
  }, [infoNutricional]);

  // ── Busca NCM ─────────────────────────────────────────────────────────────
  const [ncmSearchTerm, setNcmSearchTerm]   = useState("");
  const [showNcmResults, setShowNcmResults] = useState(false);
  const [ncmDisplay, setNcmDisplay]         = useState("");
  const debouncedNcmTerm = useDebounce(ncmSearchTerm, 400);

  const { data: ncmResults } = useQuery({
    queryKey: ["ncm-search", debouncedNcmTerm],
    queryFn: async () => {
      const { data } = await apiClient.get<{ itens: NcmItem[] }>(
        "/api/cadastros/fiscal/ncm",
        { params: { busca: debouncedNcmTerm, tamanhoPagina: 20 } }
      );
      return data.itens ?? [];
    },
    enabled: debouncedNcmTerm.length >= 2,
  });

  // Carrega o NCM ao editar (resolve CodigoInterno → código exibível)
  useEffect(() => {
    const ncmId = form.getValues("ncm_id");
    if (isEdit && ncmId > 0 && !ncmDisplay) {
      apiClient
        .get<NcmItem>(`/api/cadastros/fiscal/ncm/interno/${ncmId}`)
        .then(({ data }) => setNcmDisplay(`${data.codigo} — ${data.descricao}`))
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, produto]);

  // ── Busca CEST ────────────────────────────────────────────────────────────
  const [cestSearchTerm, setCestSearchTerm]   = useState("");
  const [showCestResults, setShowCestResults] = useState(false);
  const debouncedCestTerm = useDebounce(cestSearchTerm, 400);

  const { data: cestResults = [] } = useQuery({
    queryKey: ["cest-search", debouncedCestTerm],
    queryFn: async () => {
      const { data } = await apiClient.get<CestItem[]>("/api/cest", {
        params: { busca: debouncedCestTerm },
      });
      return data;
    },
    enabled: debouncedCestTerm.length >= 2,
  });

  // ── Similares ────────────────────────────────────────────────────────────
  interface SimilarItem { id: number; produtoSimilarId: number; descricao: string; codigoBarras?: string; }

  const { data: similares = [] } = useQuery({
    queryKey: ["produto-similares", produtoIdNum],
    queryFn: async () => {
      const { data } = await apiClient.get<SimilarItem[]>(`/api/produtos/${produtoIdNum}/similares`);
      return data;
    },
    enabled: !!produtoIdNum,
  });

  // ── Composição / Adicionais / Opcionais (editáveis) ──────────────────────
  const { itens: composicao, create: createComposicao, remove: removeComposicao } = useProdutoComposicao(id);
  const { itens: adicionais, create: createAdicional, remove: removeAdicional } = useProdutoAdicionais(id);
  const { itens: opcionais, create: createOpcional, remove: removeOpcional } = useProdutoOpcionais(id);
  const opcionaisCombo = useOpcionaisCombo();
  const { units: unidadesCombo } = useProductUnits();
  const { icms: icmsUf, createIcms, updateIcms, deleteIcms } = useProdutoIcms(id);
  const [icmsDialogOpen, setIcmsDialogOpen] = useState(false);
  const [icmsEditing, setIcmsEditing] = useState<ProdutoIcmsRow | null>(null);
  const saveIcms = (data: SalvarProdutoIcmsRequest, rid?: number) => {
    if (rid) updateIcms({ id: rid, data }); else createIcms(data);
    setIcmsDialogOpen(false);
  };
  // ── Grade (variações eixo X × eixo Y) ─────────────────────────────────────
  const { grades, createGrade, deleteGrade, gerarGrades, isGerando } = useProdutoGrades(id);
  const { gradesX } = useGradesX();
  const { gradesY } = useGradesY();
  const gradesXAtivas = (gradesX ?? []).filter(g => !g.isInativo);
  const gradesYAtivas = (gradesY ?? []).filter(g => !g.isInativo);
  const [gradeXSel, setGradeXSel] = useState(0);
  const [gradeYSel, setGradeYSel] = useState(0);
  const [gradeBarcode, setGradeBarcode] = useState("");

  // ── Saldo de estoque por local (read-only) ────────────────────────────────
  const { saldos } = useProdutoSaldo(id);

  // ── Comissionamento diferenciado por perfil ───────────────────────────────
  const { comissoes, createComissao, deleteComissao } = useProdutoComissoes(id);
  const { data: perfisCombo = [] } = usePerfisCombo();
  const COM_EMPTY = { perfilId: 0, tipoValorPercentual: 0, precoPadrao: 0, precoPromo1: 0, precoPromo2: 0, precoPromo3: 0, precoAtacado: 0, precoQuantidade: 0, precoQuantidadeAte: 0 };
  const [comNova, setComNova] = useState<{ perfilId: number; tipoValorPercentual: number; precoPadrao: number; precoPromo1: number; precoPromo2: number; precoPromo3: number; precoAtacado: number; precoQuantidade: number; precoQuantidadeAte: number }>(COM_EMPTY);
  const setCom = (k: keyof typeof COM_EMPTY, v: number) => setComNova(p => ({ ...p, [k]: v }));

  const [compProd, setCompProd] = useState(0); const [compQtd, setCompQtd] = useState(""); const [compUn, setCompUn] = useState(0);
  const [adicProd, setAdicProd] = useState(0); const [adicQtd, setAdicQtd] = useState("");
  const [opcSel, setOpcSel] = useState(0);

  // ── Preços por tabela (somente leitura) ──────────────────────────────────
  interface ProdutoPrecoItem {
    id: number;
    tabelaPrecoId: number;
    tabelaPrecoDescricao: string;
    precoPromo1: number;
    precoPromo2: number;
    precoPromo3: number;
    precoAtacado: number;
    precoQuantidade: number;
    quantidadeVenda: number;
  }

  const { data: precosTabela = [] } = useQuery({
    queryKey: ["produto-precos", produtoIdNum],
    queryFn: async () => {
      const { data } = await apiClient.get<ProdutoPrecoItem[]>(
        `/api/produtos/${produtoIdNum}/precos`
      );
      return data;
    },
    enabled: !!produtoIdNum,
  });

  // ── Busca de classificação fiscal ─────────────────────────────────────────
  const [fiscalSearchTerm, setFiscalSearchTerm]   = useState("");
  const [showFiscalResults, setShowFiscalResults] = useState(false);
  const [selectedClassif, setSelectedClassif]     = useState<ClassificacaoFiscalItem | null>(null);
  const debouncedFiscalTerm = useDebounce(fiscalSearchTerm, 400);

  const { data: fiscalResults = [] } = useQuery({
    queryKey: ["classificacoes-fiscais-search", debouncedFiscalTerm],
    queryFn: async () => {
      const { data } = await apiClient.get<{ itens: ClassificacaoFiscalItem[] }>(
        "/api/cadastros/fiscal/classificacoes-fiscais",
        { params: { busca: debouncedFiscalTerm, tamanhoPagina: 20 } },
      );
      return data.itens ?? [];
    },
    enabled: debouncedFiscalTerm.length >= 2,
  });

  // ── Formulário ────────────────────────────────────────────────────────────
  const form = useForm<ProdutoFormData>({
    resolver: zodResolver(produtoSchema),
    defaultValues: {
      descricao_completa:  "",
      descricao_resumida:  null,
      codigo_barras:       null,
      gtin:                null,
      tipo_produto:        1,
      tipo_material_id:    0,
      classificacao:       null,
      inativo:             false,
      grupo_id:            0,
      sub_grupo_id:        0,
      secao_id:            0,
      sub_secao_id:        0,
      marca_id:            0,
      modelo_id:           0,
      cor:                 null,
      tamanho:             null,
      sabor:               null,
      ncm_id:                         0,
      cest:                           null,
      aliquota_icms:                  0,
      aliquota_icms_st:               0,
      aliquota_ipi:                   0,
      percentual_tributos:            0,
      csosn_id:                       0,
      origem_id:                      0,
      cst_id:                         0,
      cclass_trib:                    null,
      id_pos:                         null,
      anp_id:                         0,
      natureza_receita_id:            0,
      cfop_saida_exterior:            0,
      cfop_entrada_exterior:          0,
      perc_pis_dif:                   0,
      perc_cofins_dif:                0,
      exportavel:                     false,
      veiculo_usado:                  false,
      aliq_cbs:                       0,
      aliq_ibs_uf:                    0,
      aliq_ibs_municipio:             0,
      valor_lucro_zero:               0,
      codigo_ml:                      null,
      garantia:                       null,
      garantia_id:                    0,
      tipo_especifico:                0,
      tipo_arma:                      null,
      serie_arma:                     null,
      serie_cano:                     null,
      descricao_anp:                  null,
      perc_gas_natural:               0,
      perc_glp:                       0,
      perc_glgn_nacional:             0,
      perc_glgn_importado:            0,
      codif:                          null,
      valor_partida:                  0,
      numero_lote:                    null,
      quantidade_lote:                0,
      data_fabricacao:                null,
      data_validade:                  null,
      preco_max_consumidor:           0,
      numero_recopi:                  null,
      mve:                            0,
      custo_medio:                    0,
      custo_gerencial:                0,
      custo_reposicao:                0,
      custos_extras:                  0,
      custo_frete:                    0,
      custo_seguro:                   0,
      custo_outras_despesas:          0,
      custo_embalagem:                0,
      perc_custo_operacional:         0,
      lucro_desejado:                 0,
      perc_lucro_cadastrado:          0,
      markup:                         0,
      preco_sugestao:                 0,
      preco_venda1:                   0,
      preco_venda2:                   0,
      preco_atacado:                  0,
      preco_promocao:                 0,
      desconto_maximo:                0,
      perc_comissao:                  0,
      comissao_por:                   0,
      movimenta_comissao_diferenciada: false,
      cst_pis_cofins_entrada_id:      0,
      cst_pis_cofins_saida_id:        0,
      cst_ipi_entrada_id:             0,
      cst_ipi_saida_id:               0,
      incide_ipi:                     true,
      incide_pis_cofins:              false,
      monofasico:                     false,
      cfop_saida:                     0,
      cfop_saida_nao_contribuinte:    0,
      cfop_saida_uf:                  0,
      cfop_saida_uf_nao_contribuinte: 0,
      cfop_entrada:                   0,
      cfop_entrada_uf:                0,
      preco_padrao:        0,
      tipo_margem_lucro:   1,
      percentual_margem:   0,
      percentual_desconto: 0,
      valor_margem:        0,
      controla_estoque:        true,
      controla_lote:           false,
      perecivel:               false,
      dias_validade:           0,
      conservacao:             null,
      rua:                     null,
      coluna:                  null,
      nivel:                   null,
      prateleira:              null,
      consulta_peso:           false,
      gera_carga_balanca:      false,
      mercadoria_producao:     false,
      imprimir_data_embalagem: false,
      imprimir_observacao:     false,
      monta_obs_ficha_tecnica: false,
      mostrar_celular:         true,
      mostrar_catalogo:        true,
      mostrar_ecommerce:       true,
      gera_comissao:           false,
      pontua_fidelidade:       false,
      possui_composicao:       false,
      possui_adicionais:       false,
      usa_grade:               false,
      observacao:          null,
      path_imagem:         null,
    },
  });

  useEffect(() => {
    if (produto) {
      form.reset(mapToFormData(produto));
    }
  }, [produto, form]);

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = (data: ProdutoFormData) => {
    const req = mapToApiRequest(data);
    if (isEdit) {
      updateMutation.mutate({ id, data: req }, {
        onSuccess: () => navigate("/produtos"),
      });
    } else {
      createMutation.mutate(req, {
        onSuccess: () => navigate("/produtos"),
      });
    }
  };

  const handleSelectClassifFiscal = (item: ClassificacaoFiscalItem) => {
    setSelectedClassif(item);
    form.setValue("ncm_id", item.ncmId);
    setShowFiscalResults(false);
    setFiscalSearchTerm(item.codigo + " — " + item.descricao);
  };

  if (isEdit && isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    );
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;
  const nomeProduto = produto?.descricao;

  return (
    <div className="p-6 space-y-6">
      {/* ── Cabeçalho ──────────────────────────────────────────────── */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/produtos")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold">
            {isEdit ? (nomeProduto ?? "Editar Produto") : "Novo Produto"}
          </h1>
          {isEdit && (
            <p className="text-sm text-muted-foreground">Cadastro de produto</p>
          )}
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <Tabs defaultValue="geral" className="w-full">
            <TabsList className="flex flex-wrap h-auto gap-1 mb-6">
              <TabsTrigger value="geral">Geral</TabsTrigger>
              <TabsTrigger value="classificacao">Classificação</TabsTrigger>
              <TabsTrigger value="fiscal">Fiscal</TabsTrigger>
              <TabsTrigger value="especifico">Específico</TabsTrigger>
              <TabsTrigger value="precos">Preços</TabsTrigger>
              <TabsTrigger value="controle">Controle</TabsTrigger>
              {isEdit && <TabsTrigger value="estoque">Estoque</TabsTrigger>}
              {isEdit && <TabsTrigger value="fornecedores">Fornecedores</TabsTrigger>}
              {isEdit && mostraSngpc && <TabsTrigger value="sngpc">SNGPC</TabsTrigger>}
              {isEdit && <TabsTrigger value="composicao">Composição</TabsTrigger>}
              {isEdit && <TabsTrigger value="adicionais">Adicionais</TabsTrigger>}
              {isEdit && <TabsTrigger value="grade">Grade</TabsTrigger>}
              {isEdit && <TabsTrigger value="comissao">Comissão</TabsTrigger>}
              {isEdit && <TabsTrigger value="contabil">Contábil</TabsTrigger>}
              <TabsTrigger value="exibicao">Exibição</TabsTrigger>
              <TabsTrigger value="integracao">Integração</TabsTrigger>
              <TabsTrigger value="observacoes">Observações</TabsTrigger>
              {isEdit && <TabsTrigger value="nutricional">Nutricional</TabsTrigger>}
            </TabsList>

            {/* ════════════════════════════════ TAB: GERAL */}
            <TabsContent value="geral">
              <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-4">
                <FormField
                  control={form.control}
                  name="descricao_completa"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descrição *</FormLabel>
                      <FormControl>
                        <Input placeholder="Descrição completa do produto" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="descricao_resumida"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Descrição Resumida</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Descrição curta (PDV / etiqueta)"
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="codigo_barras"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Código do produto</FormLabel>
                        <FormControl>
                          <Input placeholder="Código interno (vai na NF-e)" {...field} value={field.value ?? ""} />
                        </FormControl>
                        <FormDescription>
                          É o código do produto na nota fiscal. O código de barras (GTIN/EAN) tem campo próprio.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="gtin"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>GTIN</FormLabel>
                        <FormControl>
                          <Input placeholder="GTIN" {...field} value={field.value ?? ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="tipo_produto"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de Produto *</FormLabel>
                      <Select
                        value={String(field.value)}
                        onValueChange={(v) => field.onChange(Number(v))}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o tipo" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {TIPO_PRODUTO_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={String(opt.value)}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="tipo_material_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Finalidade do Produto</FormLabel>
                      <Select value={field.value ? String(field.value) : "0"} onValueChange={(v) => field.onChange(Number(v))}>
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder="Selecione a finalidade" /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="0">— Nenhuma —</SelectItem>
                          {tiposMaterial.map((m) => (
                            <SelectItem key={m.id} value={String(m.id)}>{m.descricao}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="inativo"
                  render={({ field }) => (
                    <FormItem>
                      <SwitchRow
                        label="Inativo"
                        description="Produto inativo não aparece nas vendas"
                        checked={field.value}
                        onChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />

                <div className="pt-2 flex justify-end">
                  <Button type="submit" disabled={isSaving}>
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* ════════════════════════════════ TAB: CLASSIFICAÇÃO */}
            <TabsContent value="classificacao">
              <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="grupo_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Grupo</FormLabel>
                        <Select
                          value={String(field.value)}
                          onValueChange={(v) => field.onChange(Number(v))}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o grupo" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="0">— Nenhum —</SelectItem>
                            {groups.filter((g) => !g.isInativo).map((g) => (
                              <SelectItem key={g.id} value={String(g.id)}>
                                {g.descricao}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="sub_grupo_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Subgrupo</FormLabel>
                        <Select
                          value={String(field.value)}
                          onValueChange={(v) => field.onChange(Number(v))}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o subgrupo" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="0">— Nenhum —</SelectItem>
                            {subgroups.filter((s) => !s.isInativo).map((s) => (
                              <SelectItem key={s.id} value={String(s.id)}>
                                {s.descricao}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="secao_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Seção</FormLabel>
                        <Select
                          value={String(field.value)}
                          onValueChange={(v) => field.onChange(Number(v))}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a seção" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="0">— Nenhuma —</SelectItem>
                            {sections.filter((s) => !s.isInativo).map((s) => (
                              <SelectItem key={s.id} value={String(s.id)}>
                                {s.descricao}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="sub_secao_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Subseção</FormLabel>
                        <Select
                          value={String(field.value)}
                          onValueChange={(v) => field.onChange(Number(v))}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a subseção" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="0">— Nenhuma —</SelectItem>
                            {subsections.filter((s) => !s.isInativo).map((s) => (
                              <SelectItem key={s.id} value={String(s.id)}>
                                {s.descricao}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="marca_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Marca</FormLabel>
                        <Select
                          value={String(field.value)}
                          onValueChange={(v) => field.onChange(Number(v))}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione a marca" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="0">— Nenhuma —</SelectItem>
                            {brands.map((b) => (
                              <SelectItem key={b.id} value={String(b.id)}>
                                {b.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="modelo_id"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Modelo</FormLabel>
                        <Select
                          value={String(field.value)}
                          onValueChange={(v) => field.onChange(Number(v))}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Selecione o modelo" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="0">— Nenhum —</SelectItem>
                            {models.map((m) => (
                              <SelectItem key={m.id} value={String(m.id)}>
                                {m.nome}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="classificacao"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Referência / Classificação</FormLabel>
                      <FormControl>
                        <Input placeholder="Referência interna ou classificação do produto" {...field} value={field.value ?? ""} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <FormField
                    control={form.control}
                    name="cor"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Cor</FormLabel>
                        <FormControl>
                          <Input placeholder="Ex.: Azul" {...field} value={field.value ?? ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="tamanho"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tamanho</FormLabel>
                        <FormControl>
                          <Input placeholder="Ex.: G" {...field} value={field.value ?? ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="sabor"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Sabor</FormLabel>
                        <FormControl>
                          <Input placeholder="Ex.: Morango" {...field} value={field.value ?? ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="pt-2 flex justify-end">
                  <Button type="submit" disabled={isSaving}>
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* ════════════════════════════════ TAB: FISCAL */}
            <TabsContent value="fiscal">
              <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-4">
                {/* Classificação fiscal — busca */}
                <div className="space-y-2">
                  <FormLabel>Classificação Fiscal (NCM)</FormLabel>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      className="pl-10"
                      placeholder="Digite o código NCM ou descrição para buscar..."
                      value={fiscalSearchTerm}
                      onChange={(e) => {
                        setFiscalSearchTerm(e.target.value);
                        setShowFiscalResults(true);
                      }}
                      onFocus={() => setShowFiscalResults(true)}
                    />
                    {showFiscalResults && fiscalResults.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-64 overflow-y-auto">
                        {fiscalResults.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                            onClick={() => handleSelectClassifFiscal(item)}
                          >
                            <span className="font-mono font-medium">{item.codigo}</span>
                            {" — "}
                            <span className="text-muted-foreground">{item.descricao}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {selectedClassif && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      <Badge variant="outline">NCM: {selectedClassif.codigo}</Badge>
                      {selectedClassif.incideIpi && (
                        <Badge variant="secondary">Incide IPI</Badge>
                      )}
                      {selectedClassif.incidePisCofins && (
                        <Badge variant="secondary">Incide PIS/COFINS</Badge>
                      )}
                      {selectedClassif.isMonofasico && (
                        <Badge variant="secondary">Monofásico</Badge>
                      )}
                    </div>
                  )}
                </div>

                {/* NCM — combobox de busca */}
                <FormField
                  control={form.control}
                  name="ncm_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>NCM</FormLabel>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input
                          className="pl-10"
                          placeholder="Código ou descrição NCM..."
                          value={ncmSearchTerm || ncmDisplay}
                          onChange={(e) => {
                            setNcmSearchTerm(e.target.value);
                            setNcmDisplay("");
                            setShowNcmResults(true);
                          }}
                          onFocus={() => setShowNcmResults(true)}
                          onBlur={() => setTimeout(() => setShowNcmResults(false), 200)}
                        />
                        {field.value > 0 && !ncmSearchTerm && (
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => { field.onChange(0); setNcmDisplay(""); setNcmSearchTerm(""); }}
                          >
                            ✕
                          </button>
                        )}
                        {showNcmResults && (ncmResults ?? []).length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-60 overflow-y-auto">
                            {(ncmResults ?? []).map((item) => (
                              <button
                                key={item.codigoInterno}
                                type="button"
                                className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                                onMouseDown={() => {
                                  field.onChange(item.codigoInterno);
                                  setNcmDisplay(`${item.codigo} — ${item.descricao}`);
                                  setNcmSearchTerm("");
                                  setShowNcmResults(false);
                                }}
                              >
                                <span className="font-mono font-medium">{item.codigo}</span>
                                {" — "}
                                <span className="text-muted-foreground text-xs">{item.descricao}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      {field.value > 0 && (
                        <p className="text-xs text-muted-foreground">ID interno: {field.value}</p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* CEST — combobox de busca */}
                <FormField
                  control={form.control}
                  name="cest"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>CEST</FormLabel>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input
                          className="pl-10"
                          placeholder="Código ou descrição CEST..."
                          value={cestSearchTerm || field.value || ""}
                          onChange={(e) => {
                            setCestSearchTerm(e.target.value);
                            if (!e.target.value) field.onChange(null);
                            setShowCestResults(true);
                          }}
                          onFocus={() => setShowCestResults(true)}
                          onBlur={() => setTimeout(() => setShowCestResults(false), 200)}
                        />
                        {field.value && !cestSearchTerm && (
                          <button
                            type="button"
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground hover:text-foreground"
                            onClick={() => { field.onChange(null); setCestSearchTerm(""); }}
                          >
                            ✕
                          </button>
                        )}
                        {showCestResults && cestResults.length > 0 && (
                          <div className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-md max-h-60 overflow-y-auto">
                            {cestResults.map((item) => (
                              <button
                                key={item.id}
                                type="button"
                                className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                                onMouseDown={() => {
                                  field.onChange(item.codigo);
                                  setCestSearchTerm("");
                                  setShowCestResults(false);
                                }}
                              >
                                <span className="font-mono font-medium">{item.codigo}</span>
                                {" — "}
                                <span className="text-muted-foreground text-xs">{item.descricao}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Alíquotas */}
                <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                  <FormField
                    control={form.control}
                    name="aliquota_icms"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Alíquota ICMS (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            {...field}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="aliquota_icms_st"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Alíquota ICMS-ST (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            {...field}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="aliquota_ipi"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Alíquota IPI (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            {...field}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="percentual_tributos"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>% Tributos Informado</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            {...field}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Flags fiscais */}
                <div className="space-y-3">
                  <FormField
                    control={form.control}
                    name="incide_ipi"
                    render={({ field }) => (
                      <FormItem>
                        <SwitchRow label="Incide IPI" checked={field.value} onChange={field.onChange} />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="incide_pis_cofins"
                    render={({ field }) => (
                      <FormItem>
                        <SwitchRow label="Incide PIS/COFINS" checked={field.value} onChange={field.onChange} />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="monofasico"
                    render={({ field }) => (
                      <FormItem>
                        <SwitchRow label="Monofásico" description="Produto sujeito à tributação monofásica" checked={field.value} onChange={field.onChange} />
                      </FormItem>
                    )}
                  />
                </div>

                {/* ICMS: Origem, CST, CSOSN, Classificação Tributária (Reforma) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FiscalComboField control={form.control} name="origem_id" label="Origem da Mercadoria"
                    options={origensList.map((o) => ({ id: o.id, label: `${o.codigo} — ${o.descricao}` }))} />
                  <FiscalComboField control={form.control} name="cst_id" label="CST ICMS"
                    options={cstIcmsList.map((c) => ({ id: c.id, label: `${c.cst} — ${c.descricao}` }))} />
                  <FiscalComboField control={form.control} name="csosn_id" label="CSOSN (Simples Nacional)"
                    options={csosnList.map((c) => ({ id: c.id, label: `${c.codigoCsosn} — ${c.descricao}` }))} />
                  <FormField control={form.control} name="cclass_trib" render={({ field }) => (
                    <FormItem>
                      <FormLabel>Classificação Tributária (IBS/CBS)</FormLabel>
                      <Select value={field.value || "__none__"} onValueChange={(v) => field.onChange(v === "__none__" ? null : v)}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Selecione" /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="__none__">—</SelectItem>
                          {cClassTribList.map((c) => <SelectItem key={c.id} value={c.codCClassTrib}>{c.codCClassTrib} — {c.nome}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>

                {/* CST PIS/COFINS e IPI */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FiscalComboField control={form.control} name="cst_pis_cofins_entrada_id" label="CST PIS/COFINS Entrada"
                    options={cstPisList.map((c) => ({ id: c.id, label: `${c.cst} — ${c.descricao}` }))} />
                  <FiscalComboField control={form.control} name="cst_pis_cofins_saida_id" label="CST PIS/COFINS Saída"
                    options={cstPisList.map((c) => ({ id: c.id, label: `${c.cst} — ${c.descricao}` }))} />
                  <FiscalComboField control={form.control} name="cst_ipi_entrada_id" label="CST IPI Entrada"
                    options={cstIpiList.map((c) => ({ id: c.id, label: `${c.codigo} — ${c.descricao}` }))} />
                  <FiscalComboField control={form.control} name="cst_ipi_saida_id" label="CST IPI Saída"
                    options={cstIpiList.map((c) => ({ id: c.id, label: `${c.codigo} — ${c.descricao}` }))} />
                </div>

                {/* CFOPs */}
                <div className="border-t pt-4">
                  <p className="text-sm font-semibold mb-3">CFOPs Padrão</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {(
                      [
                        { name: "cfop_saida",                 label: "CFOP Saída" },
                        { name: "cfop_saida_nao_contribuinte",label: "CFOP Saída Não Contribuinte" },
                        { name: "cfop_saida_uf",              label: "CFOP Saída UF" },
                        { name: "cfop_saida_uf_nao_contribuinte", label: "CFOP Saída UF N.Contrib." },
                        { name: "cfop_entrada",               label: "CFOP Entrada" },
                        { name: "cfop_entrada_uf",            label: "CFOP Entrada UF" },
                        { name: "cfop_saida_exterior",        label: "CFOP Saída Exterior" },
                        { name: "cfop_entrada_exterior",      label: "CFOP Entrada Exterior" },
                      ] as const
                    ).map(({ name, label }) => (
                      <FormField
                        key={name}
                        control={form.control}
                        name={name}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{label}</FormLabel>
                            <CfopSearchCombobox value={field.value || null} onChange={(v) => field.onChange(v ?? 0)} />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    ))}
                  </div>
                </div>

                {/* IBS/CBS, Natureza Receita, ANP, PIS/COFINS diferenciado */}
                <div className="border-t pt-4 space-y-4">
                  <div>
                    <p className="text-sm font-semibold mb-3">IBS / CBS (Reforma Tributária)</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <FormField control={form.control} name="aliq_cbs" render={({ field }) => (
                        <FormItem><FormLabel>Alíq. CBS (%)</FormLabel><FormControl><Input type="number" step="0.01" min="0" max="100" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="aliq_ibs_uf" render={({ field }) => (
                        <FormItem><FormLabel>Alíq. IBS UF (%)</FormLabel><FormControl><Input type="number" step="0.01" min="0" max="100" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="aliq_ibs_municipio" render={({ field }) => (
                        <FormItem><FormLabel>Alíq. IBS Município (%)</FormLabel><FormControl><Input type="number" step="0.01" min="0" max="100" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl><FormMessage /></FormItem>
                      )} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FiscalComboField control={form.control} name="natureza_receita_id" label="Natureza da Receita"
                      options={natReceitaList.map((n) => ({ id: n.id, label: `${n.codigo} — ${n.descricao}` }))}
                      placeholder={natReceitaList.length ? "Selecione" : "Tabela não populada"} />
                    <FormField control={form.control} name="anp_id" render={({ field }) => (
                      <FormItem>
                        <FormLabel>ANP (Produto Específico / Combustível)</FormLabel>
                        <FormControl>
                          <AnpSearchCombobox value={field.value || undefined} onChange={(anpId) => field.onChange(anpId)} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="perc_pis_dif" render={({ field }) => (
                      <FormItem><FormLabel>% PIS Diferenciado</FormLabel><FormControl><Input type="number" step="0.01" min="0" max="100" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="perc_cofins_dif" render={({ field }) => (
                      <FormItem><FormLabel>% COFINS Diferenciado</FormLabel><FormControl><Input type="number" step="0.01" min="0" max="100" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl><FormMessage /></FormItem>
                    )} />
                  </div>
                </div>

                {/* ICMS por UF (detalhado) */}
                {isEdit && produtoIdNum && (
                  <div className="border-t pt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-sm font-semibold">ICMS por UF (detalhado)</h3>
                        <p className="text-xs text-muted-foreground">Redução BC, MVA, modalidade, motivo desoneração e IBS/CBS por UF de destino.</p>
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={() => { setIcmsEditing(null); setIcmsDialogOpen(true); }}>
                        <Plus className="h-4 w-4 mr-1" /> Adicionar UF
                      </Button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b text-muted-foreground text-xs">
                          <th className="text-left py-2 pr-2 font-medium">UF</th>
                          <th className="text-left py-2 px-2 font-medium">CST</th>
                          <th className="text-right py-2 px-2 font-medium">Alíq. ICMS</th>
                          <th className="text-right py-2 px-2 font-medium">Alíq. ST</th>
                          <th className="text-right py-2 px-2 font-medium">MVA ST</th>
                          <th className="text-center py-2 px-2 font-medium">Destaca ST</th>
                          <th className="w-16" />
                        </tr></thead>
                        <tbody>
                          {icmsUf.length === 0 && (<tr><td colSpan={7} className="text-center text-muted-foreground py-4 text-xs">Nenhuma UF configurada. Clique em "Adicionar UF".</td></tr>)}
                          {icmsUf.map((r) => (
                            <tr key={r.id} className="border-b last:border-0">
                              <td className="py-2 pr-2 font-mono font-semibold">{r.ufIcmsSt}</td>
                              <td className="py-2 px-2 font-mono text-xs">{r.tributacaoIcms || "—"}</td>
                              <td className="py-2 px-2 text-right font-mono text-xs">{r.aliquotaIcms != null ? `${r.aliquotaIcms.toFixed(2)}%` : "—"}</td>
                              <td className="py-2 px-2 text-right font-mono text-xs">{r.aliquotaIcmsSt != null ? `${r.aliquotaIcmsSt.toFixed(2)}%` : "—"}</td>
                              <td className="py-2 px-2 text-right font-mono text-xs">{r.margemValorAdicionadoSt != null ? `${r.margemValorAdicionadoSt.toFixed(2)}%` : "—"}</td>
                              <td className="py-2 px-2 text-center text-xs">{r.destacarIcms ? "Sim" : "—"}</td>
                              <td className="py-2 text-right"><div className="flex gap-1 justify-end">
                                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setIcmsEditing(r); setIcmsDialogOpen(true); }}><Pencil className="h-3.5 w-3.5" /></Button>
                                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteIcms(r.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                              </div></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <ProdutoIcmsDialog open={icmsDialogOpen} row={icmsEditing} cstIcmsCombo={cstIcmsList} csosnCombo={csosnList} onClose={() => setIcmsDialogOpen(false)} onSave={saveIcms} />
                  </div>
                )}

                <div className="pt-2 flex justify-end">
                  <Button type="submit" disabled={isSaving}>
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* ════════════════════════════════ TAB: PRODUTO ESPECÍFICO */}
            <TabsContent value="especifico">
              <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-6">
                <div>
                  <h3 className="text-base font-semibold">Produto Específico</h3>
                  <p className="text-xs text-muted-foreground">
                    Categorias com regras fiscais especiais na NF-e (armamento, combustível, medicamento, papel imune, veículo).
                    Selecione a categoria para exibir os campos correspondentes.
                  </p>
                </div>

                <FormField
                  control={form.control}
                  name="tipo_especifico"
                  render={({ field }) => (
                    <FormItem className="max-w-sm">
                      <FormLabel>Categoria</FormLabel>
                      <Select value={String(field.value ?? 0)} onValueChange={(v) => field.onChange(Number(v))}>
                        <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="0">Nenhum (produto comum)</SelectItem>
                          <SelectItem value="1">Armamento</SelectItem>
                          <SelectItem value="2">Combustível</SelectItem>
                          <SelectItem value="3">Medicamento</SelectItem>
                          <SelectItem value="4">Papel Imune</SelectItem>
                          <SelectItem value="5">Veículo</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* ── Armamento ──────────────────────────────── */}
                {form.watch("tipo_especifico") === 1 && (
                  <div className="border-t pt-4 space-y-3">
                    <h4 className="text-sm font-semibold">Armamento</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <FormField control={form.control} name="tipo_arma" render={({ field }) => (
                        <FormItem><FormLabel>Tipo de arma</FormLabel><FormControl><Input placeholder="Ex.: Uso permitido" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="serie_arma" render={({ field }) => (
                        <FormItem><FormLabel>Série da arma</FormLabel><FormControl><Input {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="serie_cano" render={({ field }) => (
                        <FormItem><FormLabel>Série do cano</FormLabel><FormControl><Input {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                      )} />
                    </div>
                  </div>
                )}

                {/* ── Combustível ────────────────────────────── */}
                {form.watch("tipo_especifico") === 2 && (
                  <div className="border-t pt-4 space-y-3">
                    <h4 className="text-sm font-semibold">Combustível (ANP)</h4>
                    <p className="text-xs text-muted-foreground">O código ANP é definido na aba Fiscal. Aqui informe a descrição, CODIF, percentuais de gás e valor de partida.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField control={form.control} name="descricao_anp" render={({ field }) => (
                        <FormItem><FormLabel>Descrição do produto conforme ANP</FormLabel><FormControl><Input {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="codif" render={({ field }) => (
                        <FormItem><FormLabel>Código de autorização / registro (CODIF)</FormLabel><FormControl><Input {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                      )} />
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      <FormField control={form.control} name="perc_gas_natural" render={({ field }) => (
                        <FormItem><FormLabel>% Gás natural</FormLabel><FormControl><Input type="number" step="0.01" {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="perc_glp" render={({ field }) => (
                        <FormItem><FormLabel>% GLP</FormLabel><FormControl><Input type="number" step="0.01" {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="perc_glgn_nacional" render={({ field }) => (
                        <FormItem><FormLabel>% GLGNn (nacional)</FormLabel><FormControl><Input type="number" step="0.01" {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="perc_glgn_importado" render={({ field }) => (
                        <FormItem><FormLabel>% GLGNi (importado)</FormLabel><FormControl><Input type="number" step="0.01" {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <FormField control={form.control} name="valor_partida" render={({ field }) => (
                        <FormItem><FormLabel>Valor de partida (cProdANP)</FormLabel><FormControl><Input type="number" step="0.0001" {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                    </div>
                  </div>
                )}

                {/* ── Medicamento ────────────────────────────── */}
                {form.watch("tipo_especifico") === 3 && (
                  <div className="border-t pt-4 space-y-3">
                    <h4 className="text-sm font-semibold">Medicamento</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                      <FormField control={form.control} name="numero_lote" render={({ field }) => (
                        <FormItem><FormLabel>Número do lote</FormLabel><FormControl><Input {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="quantidade_lote" render={({ field }) => (
                        <FormItem><FormLabel>Quantidade no lote</FormLabel><FormControl><Input type="number" step="0.001" {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="preco_max_consumidor" render={({ field }) => (
                        <FormItem><FormLabel>Preço máx. consumidor (PMC)</FormLabel><FormControl><Input type="number" step="0.01" {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="data_fabricacao" render={({ field }) => (
                        <FormItem><FormLabel>Data de fabricação</FormLabel><FormControl><Input type="date" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="data_validade" render={({ field }) => (
                        <FormItem><FormLabel>Data de validade</FormLabel><FormControl><Input type="date" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                      )} />
                    </div>
                  </div>
                )}

                {/* ── Papel Imune ────────────────────────────── */}
                {form.watch("tipo_especifico") === 4 && (
                  <div className="border-t pt-4 space-y-3">
                    <h4 className="text-sm font-semibold">Papel Imune</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <FormField control={form.control} name="numero_recopi" render={({ field }) => (
                        <FormItem><FormLabel>Número do RECOPI</FormLabel><FormControl><Input {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="mve" render={({ field }) => (
                        <FormItem><FormLabel>MVE</FormLabel><FormControl><Input type="number" step="0.01" {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                    </div>
                  </div>
                )}

                {/* ── Veículo ────────────────────────────────── */}
                {form.watch("tipo_especifico") === 5 && (
                  <div className="border-t pt-4 space-y-3">
                    <h4 className="text-sm font-semibold">Veículo</h4>
                    <FormField control={form.control} name="veiculo_usado" render={({ field }) => (
                      <FormItem><SwitchRow label="Veículo Usado" description="Veículo usado (tributação específica de seminovos)" checked={field.value} onChange={field.onChange} /></FormItem>
                    )} />
                  </div>
                )}
              </div>
            </TabsContent>

            {/* ════════════════════════════════ TAB: PREÇOS */}
            <TabsContent value="precos">
              <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="preco_padrao"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Preço Padrão (R$)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            {...field}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="tipo_margem_lucro"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tipo de Margem</FormLabel>
                        <Select
                          value={String(field.value)}
                          onValueChange={(v) => field.onChange(Number(v))}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {TIPO_MARGEM_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={String(opt.value)}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="percentual_margem"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Margem (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            {...field}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="percentual_desconto"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Desconto Padrão (%)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            {...field}
                            onChange={(e) => field.onChange(Number(e.target.value))}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="valor_margem"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Valor da Margem (R$) — calculado</FormLabel>
                      <FormControl>
                        <Input
                          readOnly
                          className="bg-muted"
                          value={field.value?.toFixed(2) ?? "0.00"}
                          onChange={() => {}}
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="valor_lucro_zero"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>R$ Lucro Zero</FormLabel>
                      <FormControl>
                        <Input type="number" step="0.01" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* ── Custos ──────────────────────────────────── */}
                <div className="border-t pt-4 space-y-3">
                  <h4 className="text-sm font-semibold">Custos</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <FormField control={form.control} name="custo_medio" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Custo Médio (R$)</FormLabel><FormControl><Input type="number" step="0.0001" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="custo_gerencial" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Custo Gerencial (R$)</FormLabel><FormControl><Input type="number" step="0.0001" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="custo_reposicao" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Custo Reposição (R$)</FormLabel><FormControl><Input type="number" step="0.0001" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="custos_extras" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Custos Extras (R$)</FormLabel><FormControl><Input type="number" step="0.0001" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="custo_frete" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Frete (R$)</FormLabel><FormControl><Input type="number" step="0.0001" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="custo_seguro" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Seguros (R$)</FormLabel><FormControl><Input type="number" step="0.0001" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="custo_outras_despesas" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Outras Despesas (R$)</FormLabel><FormControl><Input type="number" step="0.0001" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="custo_embalagem" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Embalagem (R$)</FormLabel><FormControl><Input type="number" step="0.0001" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="perc_custo_operacional" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">% Custo Operacional</FormLabel><FormControl><Input type="number" step="0.01" min="0" max="100" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                  </div>
                </div>

                {/* ── Markup & Lucro ──────────────────────────── */}
                <div className="border-t pt-4 space-y-3">
                  <h4 className="text-sm font-semibold">Markup & Lucro</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <FormField control={form.control} name="lucro_desejado" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">% Lucro Desejado</FormLabel><FormControl><Input type="number" step="0.01" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="perc_lucro_cadastrado" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">% Lucro Cadastrado</FormLabel><FormControl><Input type="number" step="0.01" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="perc_comissao" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">% Comissão</FormLabel><FormControl><Input type="number" step="0.01" min="0" max="100" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="comissao_por" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Comissão por</FormLabel>
                        <Select value={String(field.value ?? 0)} onValueChange={(v) => field.onChange(Number(v))}>
                          <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                          <SelectContent>
                            <SelectItem value="0">Percentual (%)</SelectItem>
                            <SelectItem value="1">Valor (R$)</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )} />
                  </div>
                  <FormField control={form.control} name="movimenta_comissao_diferenciada" render={({ field }) => (
                    <FormItem><SwitchRow label="Movimenta comissão diferenciada" description="Usa regra de comissionamento específica deste produto" checked={field.value} onChange={field.onChange} /></FormItem>
                  )} />

                  {/* Visão Markup (cálculo ao vivo) */}
                  {(() => {
                    const n = (k: string) => Number(form.watch(k as never) || 0);
                    const base = (n("custo_gerencial") || n("custo_medio"))
                      + n("custo_frete") + n("custo_seguro") + n("custo_outras_despesas")
                      + n("custo_embalagem") + n("custos_extras");
                    const carga = n("perc_comissao") + n("perc_custo_operacional") + n("lucro_desejado");
                    const markup = carga < 100 ? 1 / (1 - carga / 100) : 0;
                    const sugestao = base * markup;
                    const fmt = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
                    return (
                      <div className="rounded-md border bg-muted/40 p-3 space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground">Visão Markup (cálculo automático)</p>
                        <div className="grid grid-cols-3 gap-3 text-sm">
                          <div><span className="text-xs text-muted-foreground block">Custo Total</span><span className="font-mono">{fmt(base)}</span></div>
                          <div><span className="text-xs text-muted-foreground block">Markup</span><span className="font-mono">{markup > 0 ? markup.toFixed(4) + "×" : "—"}</span></div>
                          <div><span className="text-xs text-muted-foreground block">Preço Sugerido</span><span className="font-mono font-semibold">{markup > 0 ? fmt(sugestao) : "—"}</span></div>
                        </div>
                        {markup > 0 && (
                          <div className="flex flex-wrap gap-2 pt-1">
                            <Button type="button" size="sm" variant="outline" onClick={() => { form.setValue("markup", Number(markup.toFixed(4))); form.setValue("preco_sugestao", Number(sugestao.toFixed(2))); }}>
                              Calcular sugestão
                            </Button>
                            <Button type="button" size="sm" variant="outline" onClick={() => { form.setValue("markup", Number(markup.toFixed(4))); form.setValue("preco_sugestao", Number(sugestao.toFixed(2))); form.setValue("preco_padrao", Number(sugestao.toFixed(2))); }}>
                              Aplicar ao Preço Padrão
                            </Button>
                          </div>
                        )}
                        {carga >= 100 && <p className="text-xs text-destructive">Soma de % Comissão + % Operacional + % Lucro ≥ 100% — markup inválido.</p>}
                      </div>
                    );
                  })()}
                </div>

                {/* ── Outros preços de venda ──────────────────── */}
                <div className="border-t pt-4 space-y-3">
                  <h4 className="text-sm font-semibold">Outros Preços de Venda</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <FormField control={form.control} name="preco_sugestao" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Sugestão Venda (R$)</FormLabel><FormControl><Input type="number" step="0.01" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="markup" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Markup (×)</FormLabel><FormControl><Input type="number" step="0.0001" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="desconto_maximo" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Desconto Máximo (%)</FormLabel><FormControl><Input type="number" step="0.01" min="0" max="100" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="preco_venda1" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Venda 1 (R$)</FormLabel><FormControl><Input type="number" step="0.01" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="preco_venda2" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Venda 2 (R$)</FormLabel><FormControl><Input type="number" step="0.01" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="preco_atacado" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Preço Atacado (R$)</FormLabel><FormControl><Input type="number" step="0.01" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                    <FormField control={form.control} name="preco_promocao" render={({ field }) => (
                      <FormItem><FormLabel className="text-xs">Promoção (R$)</FormLabel><FormControl><Input type="number" step="0.01" min="0" {...field} onChange={(e) => field.onChange(Number(e.target.value))} /></FormControl></FormItem>
                    )} />
                  </div>
                </div>

                {/* Preços por tabela */}
                {isEdit && precosTabela.length > 0 && (
                  <div className="border-t pt-4 space-y-2">
                    <p className="text-sm font-semibold">Preços por Tabela</p>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-muted-foreground text-xs">
                            <th className="text-left py-2 pr-3 font-medium">Tabela</th>
                            <th className="text-right py-2 px-2 font-medium">Promo 1</th>
                            <th className="text-right py-2 px-2 font-medium">Promo 2</th>
                            <th className="text-right py-2 px-2 font-medium">Promo 3</th>
                            <th className="text-right py-2 px-2 font-medium">Atacado</th>
                            <th className="text-right py-2 px-2 font-medium">Qtd. Venda</th>
                            <th className="text-right py-2 pl-2 font-medium">Preço Qtd.</th>
                          </tr>
                        </thead>
                        <tbody>
                          {precosTabela.map((p) => (
                            <tr key={p.id} className="border-b last:border-0">
                              <td className="py-1.5 pr-3 font-medium">{p.tabelaPrecoDescricao}</td>
                              <td className="py-1.5 px-2 text-right font-mono text-xs">
                                {p.precoPromo1 > 0 ? p.precoPromo1.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—"}
                              </td>
                              <td className="py-1.5 px-2 text-right font-mono text-xs">
                                {p.precoPromo2 > 0 ? p.precoPromo2.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—"}
                              </td>
                              <td className="py-1.5 px-2 text-right font-mono text-xs">
                                {p.precoPromo3 > 0 ? p.precoPromo3.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—"}
                              </td>
                              <td className="py-1.5 px-2 text-right font-mono text-xs">
                                {p.precoAtacado > 0 ? p.precoAtacado.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—"}
                              </td>
                              <td className="py-1.5 px-2 text-right font-mono text-xs">
                                {p.quantidadeVenda > 0 ? p.quantidadeVenda.toFixed(4) : "—"}
                              </td>
                              <td className="py-1.5 pl-2 text-right font-mono text-xs">
                                {p.precoQuantidade > 0 ? p.precoQuantidade.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—"}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div className="pt-2 flex justify-end">
                  <Button type="submit" disabled={isSaving}>
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* ════════════════════════════════ TAB: CONTROLE */}
            <TabsContent value="controle">
              <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-4">
                <div className="space-y-3">
                  <FormField
                    control={form.control}
                    name="controla_estoque"
                    render={({ field }) => (
                      <FormItem>
                        <SwitchRow
                          label="Controla Estoque"
                          description="Movimenta saldo em estoque"
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="controla_lote"
                    render={({ field }) => (
                      <FormItem>
                        <SwitchRow
                          label="Controla Lote"
                          description="Rastreia o produto por lote"
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="perecivel"
                    render={({ field }) => (
                      <FormItem>
                        <SwitchRow
                          label="Perecível"
                          description="Produto com prazo de validade"
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="exportavel"
                    render={({ field }) => (
                      <FormItem>
                        <SwitchRow label="Exportável" description="Produto pode ser exportado" checked={field.value} onChange={field.onChange} />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="veiculo_usado"
                    render={({ field }) => (
                      <FormItem>
                        <SwitchRow label="Veículo Usado" description="Veículo usado (tributação específica)" checked={field.value} onChange={field.onChange} />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="consulta_peso"
                    render={({ field }) => (
                      <FormItem>
                        <SwitchRow
                          label="Consulta Peso"
                          description="Integração com balança"
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="gera_carga_balanca"
                    render={({ field }) => (
                      <FormItem>
                        <SwitchRow
                          label="Gera Carga Balança"
                          description="Envia produto para balança"
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="mercadoria_producao"
                    render={({ field }) => (
                      <FormItem>
                        <SwitchRow
                          label="Mercadoria de Produção"
                          description="Produto é fabricado/produzido internamente"
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="imprimir_data_embalagem"
                    render={({ field }) => (
                      <FormItem>
                        <SwitchRow
                          label="Imprimir Data de Embalagem"
                          description="Imprime a data de embalagem na etiqueta"
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="imprimir_observacao"
                    render={({ field }) => (
                      <FormItem>
                        <SwitchRow
                          label="Imprimir Observação"
                          description="Chama observação ao emitir nota/cupom"
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="monta_obs_ficha_tecnica"
                    render={({ field }) => (
                      <FormItem>
                        <SwitchRow
                          label="Monta Obs. Ficha Técnica"
                          description="Inclui observação na ficha técnica do produto"
                          checked={field.value}
                          onChange={field.onChange}
                        />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="dias_validade"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Dias de Validade</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          {...field}
                          onChange={(e) => field.onChange(Number(e.target.value))}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="conservacao"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Conservação</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Instruções de conservação..."
                          rows={3}
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="pt-2 flex justify-end">
                  <Button type="submit" disabled={isSaving}>
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* ════════════════════════════════ TAB: ESTOQUE */}
            {isEdit && (
              <TabsContent value="estoque">
                <div className="space-y-4">
                  {/* ── Endereçamento físico (bin/WMS) ──────────────── */}
                  <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-3">
                    <div>
                      <h3 className="text-base font-semibold">Localização no depósito</h3>
                      <p className="text-xs text-muted-foreground">Endereçamento físico do produto no estoque (para separação/picking).</p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <FormField control={form.control} name="rua" render={({ field }) => (
                        <FormItem><FormLabel className="text-xs">Rua</FormLabel><FormControl><Input className="h-9" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="coluna" render={({ field }) => (
                        <FormItem><FormLabel className="text-xs">Coluna</FormLabel><FormControl><Input className="h-9" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="nivel" render={({ field }) => (
                        <FormItem><FormLabel className="text-xs">Nível</FormLabel><FormControl><Input className="h-9" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="prateleira" render={({ field }) => (
                        <FormItem><FormLabel className="text-xs">Prateleira</FormLabel><FormControl><Input className="h-9" {...field} value={field.value ?? ""} /></FormControl><FormMessage /></FormItem>
                      )} />
                    </div>
                    <div className="flex justify-end">
                      <Button type="submit" size="sm" disabled={isSaving}>
                        <Save className="h-4 w-4 mr-2" />
                        {isSaving ? "Salvando..." : "Salvar"}
                      </Button>
                    </div>
                  </div>

                  {/* ── Saldo de estoque por local (somente leitura) ── */}
                  <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-3">
                    <div>
                      <h3 className="text-base font-semibold">Saldo de estoque por local</h3>
                      <p className="text-xs text-muted-foreground">Posição atual (somente leitura), calculada pela movimentação de estoque.</p>
                    </div>
                    {saldos.length === 0 ? (
                      <p className="text-xs text-muted-foreground">Sem saldo registrado para este produto.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-muted-foreground text-xs">
                              <th className="text-left py-2 pr-3 font-medium">Local</th>
                              <th className="text-right py-2 px-3 font-medium">Saldo Atual</th>
                              <th className="text-right py-2 px-3 font-medium">Reservado</th>
                              <th className="text-right py-2 px-3 font-medium">Em Condicional</th>
                              <th className="text-right py-2 pl-3 font-medium">Disponível</th>
                            </tr>
                          </thead>
                          <tbody>
                            {saldos.map((s) => (
                              <tr key={s.localEstoqueId} className="border-b last:border-0">
                                <td className="py-2 pr-3 font-medium">{s.localDescricao}</td>
                                <td className="py-2 px-3 text-right font-mono text-xs">{s.saldoAtual.toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
                                <td className="py-2 px-3 text-right font-mono text-xs">{s.saldoReservado.toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
                                <td className="py-2 px-3 text-right font-mono text-xs">{(s.saldoCondicional ?? 0).toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
                                <td className="py-2 pl-3 text-right font-mono text-xs font-semibold">{s.saldoDisponivel.toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t font-semibold">
                              <td className="py-2 pr-3">Total</td>
                              <td className="py-2 px-3 text-right font-mono text-xs">{saldos.reduce((a, s) => a + s.saldoAtual, 0).toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
                              <td className="py-2 px-3 text-right font-mono text-xs">{saldos.reduce((a, s) => a + s.saldoReservado, 0).toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
                              <td className="py-2 px-3 text-right font-mono text-xs">{saldos.reduce((a, s) => a + (s.saldoCondicional ?? 0), 0).toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
                              <td className="py-2 pl-3 text-right font-mono text-xs">{saldos.reduce((a, s) => a + s.saldoDisponivel, 0).toLocaleString("pt-BR", { minimumFractionDigits: 4, maximumFractionDigits: 4 })}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end">
                    <Button type="button" variant="outline" size="sm" onClick={() => { setLocalEditing(null); setLocalDialogOpen(true); }}>
                      <Plus className="h-4 w-4 mr-1" /> Adicionar Local
                    </Button>
                  </div>
                  {locaisEstoque.length === 0 ? (
                    <div className="bg-card rounded-lg border p-6 text-center">
                      <p className="text-sm text-muted-foreground">
                        Nenhum local de estoque configurado. Clique em "Adicionar Local".
                      </p>
                    </div>
                  ) : (
                    locaisEstoque.map((le) => (
                      <div key={le.id} className="bg-card rounded-lg border p-4 sm:p-6 space-y-4">
                        {/* Cabeçalho do local */}
                        <div className="flex items-center gap-3">
                          <h3 className="text-base font-semibold">{le.localEstoqueDescricao}</h3>
                          <div className="flex gap-1">
                            {le.padraoEntrada && (
                              <Badge variant="secondary" className="text-xs">Padrão Entrada</Badge>
                            )}
                            {le.padraoSaida && (
                              <Badge variant="secondary" className="text-xs">Padrão Saída</Badge>
                            )}
                            {le.vendaFracionada && (
                              <Badge variant="outline" className="text-xs">Venda Fracionada</Badge>
                            )}
                          </div>
                          <div className="flex gap-1 ml-auto">
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setLocalEditing(le); setLocalDialogOpen(true); }}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteLocal(le.id)}>
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>

                        {/* Saldos e custos */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                          <div className="rounded-md bg-muted/50 p-3 space-y-0.5">
                            <p className="text-xs text-muted-foreground">Estoque Mínimo</p>
                            <p className="text-sm font-mono font-medium">{le.estoqueMinimo.toFixed(4)}</p>
                          </div>
                          <div className="rounded-md bg-muted/50 p-3 space-y-0.5">
                            <p className="text-xs text-muted-foreground">Estoque Máximo</p>
                            <p className="text-sm font-mono font-medium">{le.estoqueMaximo.toFixed(4)}</p>
                          </div>
                          <div className="rounded-md bg-muted/50 p-3 space-y-0.5">
                            <p className="text-xs text-muted-foreground">Custo Médio</p>
                            <p className="text-sm font-mono font-medium">
                              {le.custoMedio.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                            </p>
                          </div>
                          <div className="rounded-md bg-muted/50 p-3 space-y-0.5">
                            <p className="text-xs text-muted-foreground">Última Compra</p>
                            <p className="text-sm font-mono font-medium">
                              {le.ultimaCompra.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                            </p>
                          </div>
                          <div className="rounded-md bg-muted/50 p-3 space-y-0.5">
                            <p className="text-xs text-muted-foreground">Preço Reposição</p>
                            <p className="text-sm font-mono font-medium">
                              {le.precoReposicao.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                            </p>
                          </div>
                          <div className="rounded-md bg-muted/50 p-3 space-y-0.5">
                            <p className="text-xs text-muted-foreground">Preço Gerencial</p>
                            <p className="text-sm font-mono font-medium">
                              {le.precoGerencial.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                            </p>
                          </div>
                          {le.dtUltimaCompra && le.dtUltimaCompra !== "0001-01-01T00:00:00" && (
                            <div className="rounded-md bg-muted/50 p-3 space-y-0.5">
                              <p className="text-xs text-muted-foreground">Data Última Compra</p>
                              <p className="text-sm font-medium">
                                {new Date(le.dtUltimaCompra).toLocaleDateString("pt-BR")}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Fatores de conversão e dimensões */}
                        <div className="border-t pt-3">
                          <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Fatores de Conversão</p>
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            <div className="rounded-md bg-muted/50 p-3 space-y-0.5">
                              <p className="text-xs text-muted-foreground">Fat. Entrada</p>
                              <p className="text-sm font-mono">{le.fatorConversaoEntrada.toFixed(4)}</p>
                            </div>
                            <div className="rounded-md bg-muted/50 p-3 space-y-0.5">
                              <p className="text-xs text-muted-foreground">Fat. Saída</p>
                              <p className="text-sm font-mono">{le.fatorConversaoSaida.toFixed(4)}</p>
                            </div>
                            <div className="rounded-md bg-muted/50 p-3 space-y-0.5">
                              <p className="text-xs text-muted-foreground">Fat. Entrada Trib.</p>
                              <p className="text-sm font-mono">{le.fatorConversaoEntradaTributavel.toFixed(4)}</p>
                            </div>
                            <div className="rounded-md bg-muted/50 p-3 space-y-0.5">
                              <p className="text-xs text-muted-foreground">Fat. Saída Trib.</p>
                              <p className="text-sm font-mono">{le.fatorConversaoSaidaTributavel.toFixed(4)}</p>
                            </div>
                          </div>
                        </div>

                        {/* Dimensões */}
                        {(le.volume > 0 || le.peso > 0) && (
                          <div className="border-t pt-3">
                            <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">Dimensões</p>
                            <div className="grid grid-cols-2 gap-3">
                              {le.volume > 0 && (
                                <div className="rounded-md bg-muted/50 p-3 space-y-0.5">
                                  <p className="text-xs text-muted-foreground">Volume</p>
                                  <p className="text-sm font-mono">{le.volume} {le.unidadeVolume ?? ""}</p>
                                </div>
                              )}
                              {le.peso > 0 && (
                                <div className="rounded-md bg-muted/50 p-3 space-y-0.5">
                                  <p className="text-xs text-muted-foreground">Peso</p>
                                  <p className="text-sm font-mono">{le.peso} {le.unidadePeso ?? ""}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                  <p className="text-xs text-muted-foreground px-1">
                    Custos e preços são calculados pela movimentação (somente leitura). As unidades, fatores de conversão e mín/máx são editáveis.
                  </p>
                </div>
                <LocalEstoqueDialog
                  open={localDialogOpen}
                  row={localEditing}
                  onClose={() => setLocalDialogOpen(false)}
                  onSave={saveLocal}
                />
              </TabsContent>
            )}

            {/* ════════════════════════════════ TAB: FORNECEDORES */}
            {isEdit && (
              <TabsContent value="fornecedores">
                <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-4">
                  <div className="flex justify-end">
                    <Button type="button" variant="outline" size="sm" onClick={() => { setFornEditing(null); setFornDialogOpen(true); }}>
                      <Plus className="h-4 w-4 mr-1" /> Vincular Fornecedor
                    </Button>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-muted-foreground text-xs">
                          <th className="text-left py-2 pr-3 font-medium">Fornecedor</th>
                          <th className="text-left py-2 px-2 font-medium">Cód. Fornecedor</th>
                          <th className="text-left py-2 px-2 font-medium">Cód. Barras</th>
                          <th className="text-right py-2 px-2 font-medium">Preço Compra</th>
                          <th className="text-right py-2 px-2 font-medium">Lucro Esp.</th>
                          <th className="text-right py-2 px-2 font-medium">Custos</th>
                          <th className="w-16" />
                        </tr>
                      </thead>
                      <tbody>
                        {fornecedores.length === 0 && (
                          <tr><td colSpan={7} className="text-center text-muted-foreground py-6 text-xs">Nenhum fornecedor vinculado. Clique em "Vincular Fornecedor".</td></tr>
                        )}
                        {fornecedores.map((f) => (
                          <tr key={f.id} className="border-b last:border-0">
                            <td className="py-2 pr-3 font-medium">{f.fornecedorNome}</td>
                            <td className="py-2 px-2 font-mono text-xs">{f.codigoProdutoFornecedor || "—"}</td>
                            <td className="py-2 px-2 font-mono text-xs">{f.codigoBarrasFornecedor || "—"}</td>
                            <td className="py-2 px-2 text-right font-mono text-xs">{f.precoCompra != null && f.precoCompra > 0 ? f.precoCompra.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—"}</td>
                            <td className="py-2 px-2 text-right font-mono text-xs">{f.lucroEsperado != null && f.lucroEsperado > 0 ? `${f.lucroEsperado.toFixed(2)}%` : "—"}</td>
                            <td className="py-2 px-2 text-right font-mono text-xs">{f.custos != null && f.custos > 0 ? f.custos.toLocaleString("pt-BR", { style: "currency", currency: "BRL" }) : "—"}</td>
                            <td className="py-2 pl-2 text-right">
                              <div className="flex gap-1 justify-end">
                                <Button type="button" variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setFornEditing(f); setFornDialogOpen(true); }}><Pencil className="h-3.5 w-3.5" /></Button>
                                <Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteForn(f.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <FornecedorDialog open={fornDialogOpen} row={fornEditing} onClose={() => setFornDialogOpen(false)} onSave={saveForn} />
                </div>
              </TabsContent>
            )}

            {/* ════════════════════════════════ TAB: SNGPC / ANVISA */}
            {isEdit && id && mostraSngpc && (
              <TabsContent value="sngpc">
                <MedicamentoSngpc produtoId={id} />
              </TabsContent>
            )}

            {/* ════════════════════════════════ TAB: COMPOSIÇÃO */}
            {isEdit && (
              <TabsContent value="composicao">
                <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end border rounded-lg p-3 bg-muted/30">
                    <div className="md:col-span-6 space-y-1">
                      <Label className="text-xs">Matéria-Prima</Label>
                      <ProdutoSearchCombobox value={compProd || undefined} onChange={setCompProd} />
                    </div>
                    <div className="md:col-span-2 space-y-1">
                      <Label className="text-xs">Quantidade</Label>
                      <Input type="number" step="0.0001" min="0" value={compQtd} onChange={(e) => setCompQtd(e.target.value)} placeholder="0" />
                    </div>
                    <div className="md:col-span-3 space-y-1">
                      <Label className="text-xs">Unidade</Label>
                      <Select value={compUn ? String(compUn) : "__none__"} onValueChange={(v) => setCompUn(v === "__none__" ? 0 : Number(v))}>
                        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">—</SelectItem>
                          {unidadesCombo.map((u) => <SelectItem key={u.id} value={String(u.id)}>{u.sigla} — {u.descricao}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-1">
                      <Button type="button" className="w-full" disabled={!compProd} title="Adicionar"
                        onClick={() => { createComposicao({ materiaPrimaId: compProd, quantidadeProducao: parseFloat(compQtd) || 0, unidadeId: compUn }); setCompProd(0); setCompQtd(""); setCompUn(0); }}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b text-muted-foreground text-xs"><th className="text-left py-2 pr-3 font-medium">Matéria-Prima</th><th className="text-right py-2 px-3 font-medium">Quantidade</th><th className="w-12" /></tr></thead>
                      <tbody>
                        {composicao.length === 0 && (<tr><td colSpan={3} className="text-center text-muted-foreground py-6 text-xs">Nenhuma matéria-prima. Adicione acima.</td></tr>)}
                        {composicao.map((c) => (
                          <tr key={c.id} className="border-b last:border-0">
                            <td className="py-2 pr-3">{c.materiaPrimaDescricao}</td>
                            <td className="py-2 px-3 text-right font-mono text-xs">{c.quantidadeProducao.toFixed(4)}</td>
                            <td className="py-2 text-right"><Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeComposicao(c.id)}><Trash2 className="h-3.5 w-3.5" /></Button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </TabsContent>
            )}

            {/* ════════════════════════════════ TAB: ADICIONAIS */}
            {isEdit && (
              <TabsContent value="adicionais">
                <div className="space-y-4">
                  {/* Adicionais */}
                  <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-3">
                    <h3 className="text-sm font-semibold">Adicionais</h3>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end border rounded-lg p-3 bg-muted/30">
                      <div className="md:col-span-8 space-y-1">
                        <Label className="text-xs">Produto Adicional</Label>
                        <ProdutoSearchCombobox value={adicProd || undefined} onChange={setAdicProd} />
                      </div>
                      <div className="md:col-span-3 space-y-1">
                        <Label className="text-xs">Qtd. Máxima</Label>
                        <Input type="number" min="0" value={adicQtd} onChange={(e) => setAdicQtd(e.target.value)} placeholder="0" />
                      </div>
                      <div className="md:col-span-1">
                        <Button type="button" className="w-full" disabled={!adicProd} title="Adicionar"
                          onClick={() => { createAdicional({ adicionalId: adicProd, quantidadeMaxima: parseInt(adicQtd) || 0 }); setAdicProd(0); setAdicQtd(""); }}>
                          <Plus className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b text-muted-foreground text-xs"><th className="text-left py-2 pr-3 font-medium">Produto Adicional</th><th className="text-right py-2 px-3 font-medium">Qtd. Máxima</th><th className="w-12" /></tr></thead>
                        <tbody>
                          {adicionais.length === 0 && (<tr><td colSpan={3} className="text-center text-muted-foreground py-4 text-xs">Nenhum adicional. Adicione acima.</td></tr>)}
                          {adicionais.map((a) => (
                            <tr key={a.id} className="border-b last:border-0">
                              <td className="py-2 pr-3">{a.adicionalDescricao}</td>
                              <td className="py-2 px-3 text-right font-mono text-xs">{a.quantidadeMaxima}</td>
                              <td className="py-2 text-right"><Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeAdicional(a.id)}><Trash2 className="h-3.5 w-3.5" /></Button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Opcionais */}
                  <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-3">
                    <h3 className="text-sm font-semibold">Opcionais</h3>
                    <div className="flex items-end gap-3 border rounded-lg p-3 bg-muted/30">
                      <div className="flex-1 space-y-1">
                        <Label className="text-xs">Opcional</Label>
                        <Select value={opcSel ? String(opcSel) : "__none__"} onValueChange={(v) => setOpcSel(v === "__none__" ? 0 : Number(v))}>
                          <SelectTrigger><SelectValue placeholder="Selecione o opcional" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">—</SelectItem>
                            {opcionaisCombo.map((o) => <SelectItem key={o.id} value={String(o.id)}>{o.descricao}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button type="button" disabled={!opcSel} title="Vincular"
                        onClick={() => { createOpcional(opcSel); setOpcSel(0); }}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b text-muted-foreground text-xs"><th className="text-left py-2 pr-3 font-medium">Opcional</th><th className="text-right py-2 px-3 font-medium">Qtd. Máxima</th><th className="w-12" /></tr></thead>
                        <tbody>
                          {opcionais.length === 0 && (<tr><td colSpan={3} className="text-center text-muted-foreground py-4 text-xs">Nenhum opcional. Vincule acima.</td></tr>)}
                          {opcionais.map((o) => (
                            <tr key={o.id} className="border-b last:border-0">
                              <td className="py-2 pr-3">{o.opcionalDescricao}</td>
                              <td className="py-2 px-3 text-right font-mono text-xs">{o.quantidadeMaxima}</td>
                              <td className="py-2 text-right"><Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removeOpcional(o.id)}><Trash2 className="h-3.5 w-3.5" /></Button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </TabsContent>
            )}

            {/* ════════════════════════════════ TAB: GRADE */}
            {isEdit && (
              <TabsContent value="grade">
                <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-5">
                  <div>
                    <h3 className="text-base font-semibold">Grade (variações)</h3>
                    <p className="text-xs text-muted-foreground">
                      Combinações de eixo X × eixo Y (ex.: Tamanho × Cor). Os eixos são cadastrados em Produtos › Grade X / Grade Y.
                    </p>
                  </div>

                  <FormField control={form.control} name="usa_grade" render={({ field }) => (
                    <FormItem><SwitchRow label="Usa Grade" description="Este produto trabalha com grade de variações" checked={field.value} onChange={field.onChange} /></FormItem>
                  )} />

                  {form.watch("usa_grade") && (
                    <>
                      {/* Adicionar combinação */}
                      <div className="border-t pt-4 space-y-3">
                        <h4 className="text-sm font-semibold">Adicionar combinação</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
                          <div className="space-y-1">
                            <Label className="text-xs">Eixo X</Label>
                            <Select value={gradeXSel ? String(gradeXSel) : "__none__"} onValueChange={(v) => setGradeXSel(v === "__none__" ? 0 : Number(v))}>
                              <SelectTrigger className="h-9"><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">—</SelectItem>
                                {gradesXAtivas.map(g => <SelectItem key={g.id} value={String(g.id)}>{g.descricao}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Eixo Y</Label>
                            <Select value={gradeYSel ? String(gradeYSel) : "__none__"} onValueChange={(v) => setGradeYSel(v === "__none__" ? 0 : Number(v))}>
                              <SelectTrigger className="h-9"><SelectValue placeholder="—" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">—</SelectItem>
                                {gradesYAtivas.map(g => <SelectItem key={g.id} value={String(g.id)}>{g.descricao}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-1">
                            <Label className="text-xs">Cód. Barras (opcional)</Label>
                            <Input className="h-9" value={gradeBarcode} onChange={(e) => setGradeBarcode(e.target.value)} />
                          </div>
                          <Button type="button" disabled={!gradeXSel || !gradeYSel} onClick={() => {
                            createGrade({ gradeXId: gradeXSel, gradeYId: gradeYSel, codigoBarras: gradeBarcode || null, isAtivo: true });
                            setGradeXSel(0); setGradeYSel(0); setGradeBarcode("");
                          }}>Adicionar</Button>
                        </div>
                        <Button type="button" variant="outline" size="sm" disabled={isGerando || gradesXAtivas.length === 0 || gradesYAtivas.length === 0}
                          onClick={() => gerarGrades({ gradeXIds: gradesXAtivas.map(g => g.id), gradeYIds: gradesYAtivas.map(g => g.id) })}>
                          {isGerando ? "Gerando..." : `Gerar todas (${gradesXAtivas.length} × ${gradesYAtivas.length})`}
                        </Button>
                        {(gradesXAtivas.length === 0 || gradesYAtivas.length === 0) && (
                          <p className="text-xs text-amber-600">Cadastre eixos em Grade X e Grade Y antes de gerar combinações.</p>
                        )}
                      </div>

                      {/* Lista de combinações */}
                      <div className="border-t pt-4 space-y-2">
                        <p className="text-sm font-semibold">Combinações ({grades.length})</p>
                        {grades.length === 0 ? (
                          <p className="text-xs text-muted-foreground">Nenhuma combinação cadastrada.</p>
                        ) : (
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b text-muted-foreground text-xs">
                                  <th className="text-left py-2 pr-3 font-medium">Eixo X</th>
                                  <th className="text-left py-2 px-3 font-medium">Eixo Y</th>
                                  <th className="text-left py-2 px-3 font-medium">Cód. Barras</th>
                                  <th className="py-2 w-10"></th>
                                </tr>
                              </thead>
                              <tbody>
                                {grades.map(g => (
                                  <tr key={g.id} className="border-b last:border-0">
                                    <td className="py-2 pr-3">{g.gradeXDescricao}</td>
                                    <td className="py-2 px-3">{g.gradeYDescricao}</td>
                                    <td className="py-2 px-3 font-mono text-xs">{g.codigoBarras || "—"}</td>
                                    <td className="py-2 text-right"><Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteGrade(g.id)}><Trash2 className="h-3.5 w-3.5" /></Button></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  <div className="pt-2 flex justify-end">
                    <Button type="submit" disabled={isSaving}>
                      <Save className="h-4 w-4 mr-2" />
                      {isSaving ? "Salvando..." : "Salvar"}
                    </Button>
                  </div>
                </div>
              </TabsContent>
            )}

            {/* ════════════════════════════════ TAB: COMISSÃO */}
            {isEdit && (
              <TabsContent value="comissao">
                <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-5">
                  <div>
                    <h3 className="text-base font-semibold">Comissionamento diferenciado</h3>
                    <p className="text-xs text-muted-foreground">
                      Comissão por perfil de vendedor: tipo (percentual/valor) e valores por nível de preço.
                      Vale junto do "% Comissão" base definido em Preços.
                    </p>
                  </div>

                  <FormField control={form.control} name="gera_comissao" render={({ field }) => (
                    <FormItem><SwitchRow label="Produto paga comissão" description="Habilita o cálculo de comissão para este produto" checked={field.value} onChange={field.onChange} /></FormItem>
                  )} />

                  {/* Adicionar regra por perfil */}
                  <div className="border-t pt-4 space-y-3">
                    <h4 className="text-sm font-semibold">Adicionar regra por perfil</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Perfil</Label>
                        <Select value={comNova.perfilId ? String(comNova.perfilId) : "__none__"} onValueChange={(v) => setCom("perfilId", v === "__none__" ? 0 : Number(v))}>
                          <SelectTrigger className="h-9"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">—</SelectItem>
                            {perfisCombo.map(p => <SelectItem key={p.id} value={String(p.id)}>{p.nome}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Tipo</Label>
                        <Select value={String(comNova.tipoValorPercentual)} onValueChange={(v) => setCom("tipoValorPercentual", Number(v))}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Percentual (%)</SelectItem>
                            <SelectItem value="1">Valor (R$)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {([
                        ["precoPadrao", "Padrão"], ["precoPromo1", "Promo 1"], ["precoPromo2", "Promo 2"], ["precoPromo3", "Promo 3"],
                        ["precoAtacado", "Atacado"], ["precoQuantidade", "Qtd."], ["precoQuantidadeAte", "Qtd. até"],
                      ] as [keyof typeof COM_EMPTY, string][]).map(([k, label]) => (
                        <div key={k} className="space-y-1">
                          <Label className="text-xs">{label}</Label>
                          <Input type="number" step="0.01" min="0" className="h-9" value={comNova[k]} onChange={(e) => setCom(k, Number(e.target.value))} />
                        </div>
                      ))}
                    </div>
                    <Button type="button" disabled={!comNova.perfilId} onClick={() => { createComissao(comNova); setComNova(COM_EMPTY); }}>
                      <Plus className="h-4 w-4 mr-1" /> Adicionar
                    </Button>
                    {perfisCombo.length === 0 && <p className="text-xs text-amber-600">Nenhum perfil cadastrado. Cadastre perfis em Usuários › Perfis.</p>}
                  </div>

                  {/* Lista de regras */}
                  <div className="border-t pt-4 space-y-2">
                    <p className="text-sm font-semibold">Regras ({comissoes.length})</p>
                    {comissoes.length === 0 ? (
                      <p className="text-xs text-muted-foreground">Nenhuma regra de comissão cadastrada.</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b text-muted-foreground text-xs">
                              <th className="text-left py-2 pr-3 font-medium">Perfil</th>
                              <th className="text-left py-2 px-2 font-medium">Tipo</th>
                              <th className="text-right py-2 px-2 font-medium">Padrão</th>
                              <th className="text-right py-2 px-2 font-medium">Promo 1</th>
                              <th className="text-right py-2 px-2 font-medium">Promo 2</th>
                              <th className="text-right py-2 px-2 font-medium">Promo 3</th>
                              <th className="text-right py-2 px-2 font-medium">Atacado</th>
                              <th className="text-right py-2 px-2 font-medium">Qtd.</th>
                              <th className="text-right py-2 px-2 font-medium">Qtd. até</th>
                              <th className="py-2 w-10"></th>
                            </tr>
                          </thead>
                          <tbody>
                            {comissoes.map(c => (
                              <tr key={c.id} className="border-b last:border-0">
                                <td className="py-2 pr-3 font-medium">{c.perfilNome}</td>
                                <td className="py-2 px-2">{c.tipoValorPercentual === 1 ? "Valor" : "%"}</td>
                                <td className="py-2 px-2 text-right font-mono text-xs">{(c.precoPadrao ?? 0).toFixed(2)}</td>
                                <td className="py-2 px-2 text-right font-mono text-xs">{(c.precoPromo1 ?? 0).toFixed(2)}</td>
                                <td className="py-2 px-2 text-right font-mono text-xs">{(c.precoPromo2 ?? 0).toFixed(2)}</td>
                                <td className="py-2 px-2 text-right font-mono text-xs">{(c.precoPromo3 ?? 0).toFixed(2)}</td>
                                <td className="py-2 px-2 text-right font-mono text-xs">{(c.precoAtacado ?? 0).toFixed(2)}</td>
                                <td className="py-2 px-2 text-right font-mono text-xs">{(c.precoQuantidade ?? 0).toFixed(2)}</td>
                                <td className="py-2 px-2 text-right font-mono text-xs">{(c.precoQuantidadeAte ?? 0).toFixed(2)}</td>
                                <td className="py-2 text-right"><Button type="button" variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => deleteComissao(c.id)}><Trash2 className="h-3.5 w-3.5" /></Button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  <div className="pt-2 flex justify-end">
                    <Button type="submit" disabled={isSaving}>
                      <Save className="h-4 w-4 mr-2" />
                      {isSaving ? "Salvando..." : "Salvar"}
                    </Button>
                  </div>
                </div>
              </TabsContent>
            )}

            {/* ════════════════════════════════ TAB: CONTÁBIL */}
            {isEdit && (
              <TabsContent value="contabil">
                <div className="bg-card rounded-lg border p-4 sm:p-6">
                  <ProdutoContabilTable
                    registros={contabilRegistros}
                    onAdd={createContabil}
                    onDelete={deleteContabil}
                    isLoading={isCreatingContabil}
                  />
                </div>
              </TabsContent>
            )}

            {/* ════════════════════════════════ TAB: EXIBIÇÃO */}
            <TabsContent value="exibicao">
              <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-3">
                <FormField
                  control={form.control}
                  name="mostrar_celular"
                  render={({ field }) => (
                    <FormItem>
                      <SwitchRow
                        label="Mostrar no Celular"
                        description="Exibir no app mobile / vendas externas"
                        checked={field.value}
                        onChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="mostrar_catalogo"
                  render={({ field }) => (
                    <FormItem>
                      <SwitchRow
                        label="Mostrar no Catálogo"
                        description="Exibir no catálogo de produtos"
                        checked={field.value}
                        onChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="mostrar_ecommerce"
                  render={({ field }) => (
                    <FormItem>
                      <SwitchRow
                        label="Mostrar no E-commerce"
                        description="Exibir na loja virtual"
                        checked={field.value}
                        onChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gera_comissao"
                  render={({ field }) => (
                    <FormItem>
                      <SwitchRow
                        label="Gera Comissão"
                        description="Vendas deste produto geram comissão"
                        checked={field.value}
                        onChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="pontua_fidelidade"
                  render={({ field }) => (
                    <FormItem>
                      <SwitchRow
                        label="Pontua Fidelidade"
                        description="Acumula pontos no programa de fidelidade"
                        checked={field.value}
                        onChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="possui_composicao"
                  render={({ field }) => (
                    <FormItem>
                      <SwitchRow
                        label="Possui Composição"
                        description="Produto com ficha técnica de composição"
                        checked={field.value}
                        onChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="possui_adicionais"
                  render={({ field }) => (
                    <FormItem>
                      <SwitchRow
                        label="Possui Adicionais"
                        description="Produto permite adicionar itens extras (Zé Delivery, delivery próprio)"
                        checked={field.value}
                        onChange={field.onChange}
                      />
                    </FormItem>
                  )}
                />

                <div className="pt-2 flex justify-end">
                  <Button type="submit" disabled={isSaving}>
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* ════════════════════════════════ TAB: INTEGRAÇÃO */}
            <TabsContent value="integracao">
              <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-6">
                {/* POS Controle */}
                <div className="space-y-3">
                  <div>
                    <h3 className="text-base font-semibold">POS Controle</h3>
                    <p className="text-xs text-muted-foreground">Integração com a automação de PDV / balança (POS Controle).</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="id_pos"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Referência POS</FormLabel>
                          <FormControl>
                            <Input placeholder="Código do produto no PDV / balança" {...field} value={field.value ?? ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>

                {/* Mercado Livre */}
                <div className="border-t pt-4 space-y-3">
                  <div>
                    <h3 className="text-base font-semibold">Mercado Livre</h3>
                    <p className="text-xs text-muted-foreground">Código do anúncio (MLB) para sincronização com o Mercado Livre.</p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <FormField
                      control={form.control}
                      name="codigo_ml"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Código Mercado Livre (MLB)</FormLabel>
                          <FormControl>
                            <Input placeholder="Ex.: MLB1234567890" {...field} value={field.value ?? ""} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* ════════════════════════════════ TAB: OBSERVAÇÕES */}
            <TabsContent value="observacoes">
              <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-6">

                {/* ── Fotos do Produto (até 4) ───────────────────── */}
                {isEdit && produtoIdNum ? (
                  <div className="space-y-3">
                    <div>
                      <h3 className="text-sm font-semibold">Fotos do Produto (até 4)</h3>
                      <p className="text-xs text-muted-foreground">Capa (principal, em cad_produto) + Catálogo, Pedido e Delivery.</p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-2xl">
                      <ProdutoImageSlot produtoId={produtoIdNum} slot="capa"     label="Capa (principal)" hasImage={!!imagensStatus?.capa}     onUpload={(f) => uploadImagem("capa", f)}     onRemove={() => removeImagem("capa")} />
                      <ProdutoImageSlot produtoId={produtoIdNum} slot="padrao"   label="Catálogo"         hasImage={!!imagensStatus?.padrao}   onUpload={(f) => uploadImagem("padrao", f)}   onRemove={() => removeImagem("padrao")} />
                      <ProdutoImageSlot produtoId={produtoIdNum} slot="pedido"   label="Pedido"           hasImage={!!imagensStatus?.pedido}   onUpload={(f) => uploadImagem("pedido", f)}   onRemove={() => removeImagem("pedido")} />
                      <ProdutoImageSlot produtoId={produtoIdNum} slot="delivery" label="Delivery"         hasImage={!!imagensStatus?.delivery} onUpload={(f) => uploadImagem("delivery", f)} onRemove={() => removeImagem("delivery")} />
                    </div>
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                    Salve o produto primeiro para poder enviar as fotos.
                  </div>
                )}

                {/* ── Imagem principal ───────────────────────────── */}
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold">Imagem do Produto</h3>
                  <FormField
                    control={form.control}
                    name="path_imagem"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>URL / Caminho da Imagem</FormLabel>
                        <FormControl>
                          <Input
                            placeholder="https://... ou caminho relativo"
                            {...field}
                            value={field.value ?? ""}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  {/* Preview */}
                  {form.watch("path_imagem") && (
                    <div className="mt-2">
                      <img
                        src={form.watch("path_imagem") ?? ""}
                        alt="Imagem do produto"
                        className="max-h-48 max-w-xs rounded-lg border object-contain bg-muted"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                      />
                    </div>
                  )}
                </div>

                {/* ── Garantia ───────────────────────────────────── */}
                <FormField
                  control={form.control}
                  name="garantia_id"
                  render={({ field }) => (
                    <FormItem className="max-w-sm">
                      <FormLabel>Garantia</FormLabel>
                      <Select value={field.value ? String(field.value) : "__none__"} onValueChange={(v) => field.onChange(v === "__none__" ? 0 : Number(v))}>
                        <FormControl><SelectTrigger><SelectValue placeholder="Nenhuma" /></SelectTrigger></FormControl>
                        <SelectContent>
                          <SelectItem value="__none__">— Nenhuma —</SelectItem>
                          {garantias.filter(g => !g.isInativo || g.id === field.value).map(g => (
                            <SelectItem key={g.id} value={String(g.id)}>
                              {g.descricao}{g.diasGarantia ? ` (${g.diasGarantia} dias)` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* ── Observações ────────────────────────────────── */}
                <FormField
                  control={form.control}
                  name="observacao"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Observações Internas</FormLabel>
                      <FormControl>
                        <Textarea
                          placeholder="Observações internas sobre o produto..."
                          rows={6}
                          {...field}
                          value={field.value ?? ""}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Similares */}
                {isEdit && similares.length > 0 && (
                  <div className="border-t pt-4 space-y-2">
                    <p className="text-sm font-semibold">Produtos Similares / Substitutos</p>
                    <div className="flex flex-wrap gap-2">
                      {similares.map((s) => (
                        <div key={s.id} className="rounded-md border bg-muted/50 px-3 py-2 text-sm">
                          <p className="font-medium">{s.descricao}</p>
                          {s.codigoBarras && (
                            <p className="text-xs text-muted-foreground font-mono">{s.codigoBarras}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="pt-2 flex justify-end">
                  <Button type="submit" disabled={isSaving}>
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? "Salvando..." : "Salvar"}
                  </Button>
                </div>
              </div>
            </TabsContent>
            {/* ════════════════════════════════ TAB: NUTRICIONAL */}
            {isEdit && (
              <TabsContent value="nutricional">
                <NutricionalTab
                  data={nutri}
                  onChange={setNutri}
                  onSave={() => saveInfoNutricional(nutri)}
                  isSaving={isSavingNutri}
                />
              </TabsContent>
            )}
          </Tabs>
        </form>
      </Form>
    </div>
  );
};

// ── Tab de Informação Nutricional ─────────────────────────────────────────────

const UNIDADES_PORCAO = ["g", "ml", "mg", "kcal", "kJ"];
const MEDIDAS_CASEIRAS = [
  "Colher de sopa",
  "Colher de chá",
  "Xícara",
  "Copo",
  "Prato",
  "Fatia",
  "Unidade",
  "Tablete",
  "Sachê",
  "Porção",
];
const PARTES_DECIMAIS = ["Inteiro (0 decimais)", "Meia unidade (0,5)", "Um quarto (0,25)"];

type NField = keyof InfoNutricionalRequest;

function NutriRow({
  label,
  qtdKey,
  perKey,
  data,
  onChange,
}: {
  label: string;
  qtdKey: NField;
  perKey: NField;
  data: InfoNutricionalRequest;
  onChange: (v: Partial<InfoNutricionalRequest>) => void;
}) {
  return (
    <tr className="border-b last:border-0">
      <td className="py-2 pr-4 text-sm font-medium">{label}</td>
      <td className="py-1 pr-2">
        <input
          type="number"
          step="0.0001"
          min={0}
          value={data[qtdKey] as number}
          onChange={(e) => onChange({ [qtdKey]: parseFloat(e.target.value) || 0 })}
          className="w-28 rounded-md border border-input bg-background px-2 py-1 text-sm text-right ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </td>
      <td className="py-1">
        <input
          type="number"
          step="0.0001"
          min={0}
          value={data[perKey] as number}
          onChange={(e) => onChange({ [perKey]: parseFloat(e.target.value) || 0 })}
          className="w-24 rounded-md border border-input bg-background px-2 py-1 text-sm text-right ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </td>
    </tr>
  );
}

function NutriRowQtd({
  label,
  qtdKey,
  data,
  onChange,
}: {
  label: string;
  qtdKey: NField;
  data: InfoNutricionalRequest;
  onChange: (v: Partial<InfoNutricionalRequest>) => void;
}) {
  return (
    <tr className="border-b last:border-0">
      <td className="py-2 pr-4 text-sm font-medium">{label}</td>
      <td className="py-1 pr-2">
        <input
          type="number"
          step="0.0001"
          min={0}
          value={data[qtdKey] as number}
          onChange={(e) => onChange({ [qtdKey]: parseFloat(e.target.value) || 0 })}
          className="w-28 rounded-md border border-input bg-background px-2 py-1 text-sm text-right ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </td>
      <td className="py-1 text-xs text-muted-foreground pl-2">—</td>
    </tr>
  );
}

function NutricionalTab({
  data,
  onChange,
  onSave,
  isSaving,
}: {
  data: InfoNutricionalRequest;
  onChange: (v: InfoNutricionalRequest) => void;
  onSave: () => void;
  isSaving: boolean;
}) {
  const update = (patch: Partial<InfoNutricionalRequest>) =>
    onChange({ ...data, ...patch });

  return (
    <div className="bg-card rounded-lg border p-4 sm:p-6 space-y-6">
      <p className="text-sm text-muted-foreground">
        Informações nutricionais conforme padrão ANVISA para rotulagem de alimentos.
      </p>

      {/* ── Porção ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="space-y-1">
          <label className="text-sm font-medium">Quantidade por Porção</label>
          <input
            type="number"
            step="0.0001"
            min={0}
            value={data.qtdPorcao}
            onChange={(e) => update({ qtdPorcao: parseFloat(e.target.value) || 0 })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Unidade da Porção</label>
          <select
            value={data.unidadePorcaoId}
            onChange={(e) => update({ unidadePorcaoId: parseInt(e.target.value) })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {UNIDADES_PORCAO.map((u, i) => (
              <option key={i} value={i}>{u}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Parte Decimal</label>
          <select
            value={data.parteDecimalId}
            onChange={(e) => update({ parteDecimalId: parseInt(e.target.value) })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {PARTES_DECIMAIS.map((p, i) => (
              <option key={i} value={i}>{p}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Medida Caseira (Qtd)</label>
          <input
            type="number"
            step="0.0001"
            min={0}
            value={data.qtdPorcaoMedidaCaseira}
            onChange={(e) => update({ qtdPorcaoMedidaCaseira: parseFloat(e.target.value) || 0 })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Tipo de Medida Caseira</label>
          <select
            value={data.medidaCaseiraId}
            onChange={(e) => update({ medidaCaseiraId: parseInt(e.target.value) })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {MEDIDAS_CASEIRAS.map((m, i) => (
              <option key={i} value={i}>{m}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Qtd por Embalagem</label>
          <input
            type="number"
            min={0}
            value={data.qtdPorcaoEmbalagem}
            onChange={(e) => update({ qtdPorcaoEmbalagem: parseInt(e.target.value) || 0 })}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
      </div>

      {/* ── Opções ──────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-6">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={data.calculoAutomaticoQtdPorcao}
            onChange={(e) => update({ calculoAutomaticoQtdPorcao: e.target.checked })}
            className="rounded border-input"
          />
          Calcular porção automaticamente pela embalagem
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={data.imprimeLactoseGalactose}
            onChange={(e) => update({ imprimeLactoseGalactose: e.target.checked })}
            className="rounded border-input"
          />
          Imprimir Lactose e Galactose
        </label>
      </div>

      {/* ── Tabela nutricional ──────────────────────────────────────── */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b">
              <th className="text-left text-sm font-semibold py-2 pr-4">Nutriente</th>
              <th className="text-right text-sm font-semibold py-2 pr-2 w-36">Quantidade</th>
              <th className="text-right text-sm font-semibold py-2 w-28">%VD</th>
            </tr>
          </thead>
          <tbody>
            <NutriRow label="Calorias (kcal)"       qtdKey="qtdCalorias"           perKey="perCalorias"           data={data} onChange={update} />
            <NutriRow label="Carboidratos (g)"       qtdKey="qtdCarboidrato"        perKey="perCarboidrato"        data={data} onChange={update} />
            <NutriRow label="Açúcares Totais (g)"    qtdKey="qtdAcucaresTotais"     perKey="perAcucaresTotais"     data={data} onChange={update} />
            <NutriRow label="Açúcares Adicionados (g)" qtdKey="qtdAcucaresAdicionados" perKey="perAcucaresAdicionados" data={data} onChange={update} />
            <NutriRow label="Fibra Alimentar (g)"    qtdKey="qtdFibraAlimentar"     perKey="perFibraAlimentar"     data={data} onChange={update} />
            <NutriRow label="Gorduras Totais (g)"    qtdKey="qtdGorduraTotais"      perKey="perGorduraTotais"      data={data} onChange={update} />
            <NutriRow label="Gorduras Saturadas (g)" qtdKey="qtdGorduraSaturada"    perKey="perGorduraSaturada"    data={data} onChange={update} />
            <NutriRow label="Gorduras Trans (g)"     qtdKey="qtdGorduraTrans"       perKey="perGorduraTrans"       data={data} onChange={update} />
            <NutriRow label="Proteínas (g)"          qtdKey="qtdProteinas"          perKey="perProteinas"          data={data} onChange={update} />
            <NutriRow label="Sódio (mg)"             qtdKey="qtdSodio"              perKey="perSodio"              data={data} onChange={update} />
            <NutriRow label="Cálcio (mg)"            qtdKey="qtdCalcio"             perKey="perCalcio"             data={data} onChange={update} />
            <NutriRow label="Ferro (mg)"             qtdKey="qtdFerro"              perKey="perFerro"              data={data} onChange={update} />
            <NutriRowQtd label="Vitamina A (mcg RAE)"   qtdKey="qtdVitaminaA"  data={data} onChange={update} />
            <NutriRowQtd label="Vitamina C (mg)"        qtdKey="qtdVitaminaC"  data={data} onChange={update} />
            <NutriRowQtd label="Lactose (g)"            qtdKey="qtdLactose"    data={data} onChange={update} />
            <NutriRowQtd label="Galactose (g)"          qtdKey="qtdGalactose"  data={data} onChange={update} />
          </tbody>
        </table>
      </div>

      <div className="pt-2 flex justify-end">
        <Button onClick={onSave} disabled={isSaving} type="button">
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? "Salvando..." : "Salvar Nutricional"}
        </Button>
      </div>
    </div>
  );
}

export default ProdutoFormPage;
